import React from 'react';
import { StatusDot } from './DaySummary';
import { margenDe, seExcede } from '../../lib/exceso';
import { leerMacro, claseDelMacro, fondoDelMacro, llevaPunto, SUELO_CUADRADO } from '../../lib/estadoDelMacro';
import { num1, alDecima } from '../../lib/numeros';
import ContadorFamilia from './ContadorFamilia';
import MenuDeLaPantalla from './MenuDeLaPantalla';
import { favoritasDeLaComida } from '../../lib/favoritasDeLaComida';
import { pasoDelCampo, unidadesRedondas, unidadesParaVer, minimoPorUnidad } from '../../lib/pasoDeCantidad';
import {
    ChevronDown, ChevronUp, ChevronRight, Plus, Trash2, Minus, Zap, Pointer, RefreshCw, ArrowUp, ArrowUpDown, Lock, Star, X, Check,
    ArrowRight
} from 'lucide-react';
import { ahoraDeLaApp } from '../../lib/horaEspana';

// AQUÍ ESTABA `MACRO`, el color de cada macro (#FF671F la proteína, #2196F3 los hidratos,
// #FFA500 la grasa). Lo único que lo usaba eran las tres barras del selector de comidas, y
// desde la ficha 02 del 5-09-2026 esas barras van en gris. Se queda apuntado para que nadie
// vuelva a colorearlas «porque antes tenían color»: fue él quien lo quitó.

/**
 * EN QUÉ PUNTO ESTÁ ESTA COMIDA, con la palabra que usa el documento del 10-08.
 *
 * «Sin hacer» y «Cuadrada» son las suyas, y «Montar» se cambió por «Sin hacer»
 * a petición de Jesús. Van al final de la fila, que es lo que convierte la lista en algo
 * que se va tachando.
 *
 * TRES ESTADOS, LOS MISMOS QUE EN NUTRICIÓN (Francisco, 17-08): por debajo del margen
 * «Te falta», por encima «Te pasas», y dentro «Cuadrada». Se quita «Válida».
 *
 * Estaba separado «Cuadrada» (clavada, menos de medio gramo) de «Válida» (dentro del
 * margen pero no clavada), siguiendo el documento del 10-08. En la pantalla eso se leía
 * como que había dos varas: una comida con 10,5 g de grasa sobre 10 salía «Válida» y
 * parecía peor que otra idéntica, cuando el método la da por buena igual. El margen es
 * ±4 g y es el mismo en todas partes; si algo cae dentro, cuadra.
 *
 * En el perientreno la grasa no cuenta, igual que en el resto del cálculo.
 */
const NOMBRE_MACRO = { P: 'proteína', H: 'hidratos', G: 'grasa' };
const enumerar = (p) => (p.length <= 1 ? p.join('') : `${p.slice(0, -1).join(', ')} y ${p[p.length - 1]}`);

const estadoDeLaComida = (status, target, served, cuantosAlimentos, esPeri = false, bloqueada = false) => {
    // Con el volcado puesto, las demás comidas quedan con objetivo = servido y salían
    // como «Cuadrada» en verde mientras el día decía «te falta» (punto 11 del 23-08:
    // «una de las dos cosas está mal»). La que mentía era el verde: bloqueada no es
    // cuadrada, es que ya no juega.
    if (bloqueada) return { texto: 'bloqueada', color: 'apagado' };
    // «SIN CREAR» EN GRIS Y SIN PUNTO (punto 196 del 27-08, que corrige el 116 y el 117).
    //
    // Iba en naranja porque el 117 decía «lo que te pide algo se ve», y se escribió ANTES de
    // cerrar la regla del color del punto 76: el naranja es sólo para lo que se pasa. Una
    // comida sin crear no es un error -- es que aún no la has hecho --, y con el naranja
    // puesto un día recién empezado salía entero en color de aviso sin haber nada mal.
    //
    // Y sin punto: «faltan 11 de proteína» es un número y lleva el suyo; «sin crear» no es un
    // número, es una etiqueta que dice que ahí no hay nada.
    if (!cuantosAlimentos) return { texto: 'sin crear', color: 'apagado' };
    // Neutra: se desvía de su objetivo, pero el total del día ya cuadra (ver
    // `getMealStatusParaVer` en NutritionPage). No es un aviso: el día manda.
    if (status === 'neutra') return { texto: 'el día ya cuadra', color: 'apagado' };

    const claves = esPeri ? ['P', 'H'] : ['P', 'H', 'G'];
    // EL MARGEN ES EL DE `margenDe` (lib/exceso): desde el 3-09, los 4 g planos en todo
    // (decisión de Francisco, la letra del 11.1); antes era proporcional y ya no. Y es
    // INCLUSIVO: «de 1 a 4, falte o sobre, es válido», así que fuera es MÁS de 4, no >=.
    // LA MISMA RESTA QUE DENTRO DE LA COMIDA (punto 200 del 28-08). Dentro, el objetivo se
    // enseña al medio gramo y lo servido a la décima, y lo que falta se cuenta contra ESOS
    // (regla del 17-08: «quien escriba una diferencia al lado de dos cifras redondeadas
    // tiene que restar esas»). Aquí se restaban los valores exactos, así que el mismo dato
    // salía como «faltan 7,6» fuera y «faltan 7,8» dentro. Se cuenta igual en los dos
    // sitios, con el mismo margen, o la lista y la comida se contradicen.
    const meta = (k) => alDecima(target[k] || 0);
    const desvios = claves.map((k) => ({ k, d: alDecima(served[k] || 0) - meta(k) }));
    const fuera = desvios.filter((x) => Math.abs(x.d) > margenDe(meta(x.k)));

    // POR CUÁNTO Y DE QUÉ, no solo que te pasas (Jesús, 13-08): «la app enseña los dos
    // números pero no la diferencia; el cliente tiene que restar».
    //
    // Y SE CANTAN LOS DOS, LO QUE SOBRA Y LO QUE FALTA (punto 198 del 28-08). Aquí había un
    // `if` por estado: si la comida se pasaba, se decía sólo lo que sobraba y lo que faltaba
    // se perdía. Comprobado en la Comida 4 del 27: fuera ponía «sobran 7 de hidratos» y
    // dentro había además «faltan 5 de grasa». «Si el cliente lee la lista y quita hidratos,
    // cuadra los hidratos y sigue faltándole grasa sin que nadie se lo haya dicho.»
    //
    // El que SOBRA manda y va primero, con su punto y en naranja, porque es un error ya
    // cometido: hay comida de más en el plato. El que falta va detrás, sin punto y sin color,
    // porque no es un error, es trabajo por hacer.
    //
    // CADA UNO EN SU LÍNEA, y dentro de cada uno los macros con comas. Es la maqueta tal
    // cual («ocupa una línea más, y sólo en las comidas que tengan dos cosas») con la
    // corrección del 28 por la mañana encima: la que se queda en UNA línea es la
    // enumeración de varios macros del mismo tipo -- «faltan 11 de proteína, 13 de hidratos
    // y 8 de grasa» --, no la mezcla de los dos tipos.
    //
    // Y CON SU DECIMAL, EL MISMO NÚMERO QUE DENTRO (punto 200 del 28-08). Esto redondeaba a
    // entero (`num0`) y dentro de la comida no, así que el mismo dato salía como 7 fuera y
    // 6,5 dentro: «parece que están hablando de cosas distintas». Manda el de dentro, que es
    // el exacto. El punto 115 quitó decimales, pero era a los OBJETIVOS («53 · 10 · 15»), no
    // al estado; y el 122 dice que el número de arriba no se toca nunca.
    if (status === 'sobra' || status === 'falta') {
        const sobran = fuera.filter((x) => x.d > 0 && seExcede(x.k, served[x.k], target[x.k]));
        // «FALTAN 11 DE PROTEÍNA» VA EN BLANCO, NO EN NARANJA (punto 196 del 27-08, que remite
        // al 82). Es la regla de color de la app entera, la del punto 76: verde si ese macro ya
        // está resuelto, naranja si te has PASADO, y sin color mientras vas por debajo. Ir corto
        // no es un error, es que todavía no has terminado.
        const faltan = fuera.filter((x) => x.d < 0);
        const partes = [];
        if (sobran.length) {
            partes.push({
                texto: `sobran ${enumerar(sobran.map((x) => `${num1(x.d)} de ${NOMBRE_MACRO[x.k]}`))}`,
                color: 'pasado',
            });
        }
        if (faltan.length) {
            partes.push({
                texto: `faltan ${enumerar(faltan.map((x) => `${num1(-x.d)} de ${NOMBRE_MACRO[x.k]}`))}`,
                color: null,
            });
        }
        // «FALTAN» A SECAS NO DICE NADA (Francisco, 31-08-2026): «dice faltan, pero no dice el
        // qué».
        //
        // Aquí se ponía la palabra sola cuando ningún macro llegaba a salirse de su margen. Y
        // eso pasa porque hay DOS criterios midiendo lo mismo: `getMealStatus` resta los
        // valores exactos y esto resta los que se ven (objetivo al medio gramo, servido a la
        // décima, regla del 17-08). En el borde se contradicen: con un objetivo de 32,6 g de
        // proteína el medio gramo lo baja a 32,5, y un desvío de 4,0 -- que para el estado ya
        // es «falta» -- se queda en 3,9 y aquí no entra. La comida decía «faltan» y no había
        // nada que enseñar.
        //
        // Se nombra igualmente el que más lejos esté, aunque no llegue al margen. Un número
        // pequeño al lado de la palabra es siempre mejor que la palabra sola: dice de qué va
        // y deja ver que es poco. La causa de fondo -- las dos restas -- queda apuntada.
        if (!partes.length) {
            const peor = desvios.slice().sort((a, b) => Math.abs(b.d) - Math.abs(a.d))[0];
            const cuanto = peor ? num1(Math.abs(peor.d)) : null;
            partes.push(status === 'sobra'
                ? { texto: cuanto ? `sobran ${cuanto} de ${NOMBRE_MACRO[peor.k]}` : 'sobran',
                    color: 'pasado' }
                : { texto: cuanto ? `faltan ${cuanto} de ${NOMBRE_MACRO[peor.k]}` : 'faltan',
                    color: null });
        }
        return { partes, texto: partes.map((p) => p.texto).join('\n'), color: partes[0].color };
    }
    // Cuadrada. Si clava, se dice y ya; si baila dentro del margen, se dice cuánto, con las
    // palabras de la parte 2: «válido +2».
    //
    // CON EL MISMO SUELO Y EL MISMO DECIMAL QUE LA COMIDA ABIERTA (Francisco, 3-09). Aquí el
    // desvío se redondeaba a entero, así que la MISMA comida decía «válido +1» plegada y
    // «cuadrado» abierta: 0,7 g redondea a 1 en esta línea y no llegaba al suelo en la otra.
    // Dos criterios midiendo lo mismo, que es justo lo que se vino a quitar. Manda
    // `SUELO_CUADRADO`, que es de donde sale la palabra en el resto de la app.
    const mayor = desvios.slice().sort((a, b) => Math.abs(b.d) - Math.abs(a.d))[0];
    if (!mayor || Math.abs(mayor.d) < SUELO_CUADRADO) return { texto: 'cuadrada', color: 'ok' };
    // Y SE DICE DE QUÉ MACRO ES (Jesús, «Cuando la dieta está lista», 9-09): «ahora pone
    // "válido −3,7" y no dice de qué. Debería poner "válido · grasa −3,7", y los dos si hay
    // dos desviados». Un número suelto obliga a mirar arriba para saber a qué se refiere,
    // y cuando hay dos desviados no había forma de saberlo.
    const dentro = desvios
        .filter((x) => Math.abs(x.d) >= SUELO_CUADRADO)
        .sort((a, b) => Math.abs(b.d) - Math.abs(a.d))
        .slice(0, 2)
        .map((x) => `${NOMBRE_MACRO[x.k]} ${x.d > 0 ? '+' : '−'}${num1(Math.abs(x.d))}`);
    // «VÁLIDA», NO «VÁLIDO» (Francisco, 26-09): habla de la comida, como «cuadrada».
    return { texto: `válida · ${dentro.join(' · ')}`, color: 'ok' };
};

// El punto de color que va delante de la palabra del estado (punto 116).
/**
 * LOS CUATRO ESTADOS DE UNA COMIDA (punto 196 del 27-08). La lista, con sus palabras:
 *
 *     ● cuadrada        verde
 *     ● válido +2       verde
 *     ● sobran 6 de grasa   #FF5A2E
 *       faltan 11 de proteína   en blanco, «que es un número y va con su número»
 *       sin crear         en gris, y SIN punto
 *
 * O sea tres tonos y no dos, y el punto solo lo llevan los dos primeros: el punto marca que
 * hay algo que mirar, y ni lo que te falta ni lo que no has empezado lo son.
 *
 * `null` es «vas por debajo» -- blanco, la regla de color del punto 76 -- y `apagado` es «aquí
 * no hay nada»: la comida sin crear y la bloqueada.
 */
const llevaPuntoDeEstado = (color) => color === 'ok' || color === 'pasado';

const PuntoDeEstado = ({ color }) => (llevaPuntoDeEstado(color) ? (
    <span className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${color === 'ok' ? 'bg-ok' : 'bg-pasado'}`} />
) : null);

const claseDelEstado = (color) => (
    color === 'ok' ? 'text-ok'
    : color === 'pasado' ? 'text-pasado'
    : color === 'apagado' ? 'text-muted-foreground'
    : 'text-foreground');

// Los números con coma decimal y sin decimales cuando son cero, en un solo sitio para toda
// la pantalla (Jesús, 15-08, fallo 43: «34.2/37.5g»).
// UNA SOLA REGLA DE REDONDEO: la décima, para lo servido y para el objetivo (Francisco,
// 3-09). `fmtHalf` era el medio gramo de Calma y por eso el objetivo de una comida salía
// «32,5» aquí y «32,6» en la fila de Inicio. Se deja el nombre porque lo usan varias
// líneas, pero ya apunta al mismo sitio que `fmt1`.
const fmtHalf = num1;
const fmt1 = num1;

// «NO APORTA MACROS», NO «SIN MACROS» (Jesús, en la reunión con Gonzalo, minuto 11:59):
// «en las verduras quiero que ponga, no sin macro, sino que no aporta macro». No es lo mismo:
// «sin macros» suena a que falta el dato, y lo que pasa es que ese alimento no aporta nada.
// El modal de construir la comida ya lo decía así, o sea que la app decía las dos cosas a la
// vez según la pantalla. Aquí, en un solo sitio, para las dos versiones de la línea.
const SIN_APORTE = 'No aporta macros';

// UNA SOLA VERSIÓN, CON PALABRAS, EN TODOS LOS ANCHOS (ficha 27 del parte de Gonzalo,
// 5-09). Aquí había una corta para el teléfono («24P · 12H») y esta larga para el
// ordenador, y el cliente leía dos formatos del mismo dato según el aparato. Se queda la
// larga; en el teléfono, si no cabe al lado de los controles, baja a su propia línea
// debajo del nombre (ver la fila del ingrediente).
const macrosLine = (m) => {
    const parts = [
        (m.P || 0) > 0 && `${fmt1(m.P)} g proteína`,
        (m.H || 0) > 0 && `${fmt1(m.H)} g hidratos`,
        (m.G || 0) > 0 && `${fmt1(m.G)} g grasa`,
    ].filter(Boolean);
    return parts.length ? parts.join(' · ') : SIN_APORTE;
};

// ===== Selector item (master-detail) =====
export const MealSelectorItem = ({ mealKey, mealInfo, getMealTarget, calculateMealMacros, getMealStatus, isLocked, selected, onSelect, numAlimentos = 0 }) => {
    const info = mealInfo[mealKey];
    const isPeri = mealKey === 'Intra' || mealKey === 'Post';
    const target = getMealTarget(mealKey);
    const served = calculateMealMacros(mealKey);
    const status = getMealStatus(mealKey);
    const bars = [
        { v: served.P, t: target.P },
        { v: served.H, t: target.H },
    ];
    if (!isPeri) bars.push({ v: served.G, t: target.G });

    return (
        // `data-vacia`: la comida sin ningún alimento (por número, no por macros: un café solo no
        // aporta y no la deja vacía) y sin candado. El recorrido abre la primera así: en el
        // ordenador la columna no dice «sin crear», y sin esto abría una comida ya hecha.
        <button onClick={onSelect} data-testid={`meal-select-${mealKey}`}
            data-vacia={!numAlimentos && !isLocked ? 'si' : undefined}
            className={`text-left rounded-xl p-3 transition-all w-full border ${selected ? 'border-brand bg-brand/5 ring-1 ring-brand/40' : 'border-border bg-card hover:border-foreground/15'} ${isLocked ? 'opacity-60' : ''}`}>
            <div className="flex items-center gap-2.5">
                <div className={`w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0 font-heading font-bold text-sm ${isPeri ? 'bg-brand/10 text-brand' : 'bg-muted text-foreground'}`}>
                    {isPeri ? <Zap className="w-4 h-4" /> : info.shortName}
                </div>
                <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-1.5">
                        <span className="font-bold text-foreground text-sm truncate">{info.name}</span>
                        <StatusDot status={status} className="flex-shrink-0" />
                        {isLocked && <Lock className="w-3 h-3 text-amber-500 flex-shrink-0" />}
                    </div>
                    {/* CON SU LETRA (Francisco, 8-09-2026): el mismo objetivo que dentro de
                        la comida, escrito igual. */}
                    <span className="text-[10px] text-muted-foreground font-data">
                        {isPeri ? `${fmtHalf(target.P)}P · ${fmtHalf(target.H)}H` : `${fmtHalf(target.P)}P · ${fmtHalf(target.H)}H · ${fmtHalf(target.G)}G`}
                    </span>
                </div>
            </div>
            {/* LAS TRES BARRAS, SIN COLOR (Jesús, ficha 02 del 5-09-2026): «todo igual pero
                sin los colores; las barras se quedan donde están y con el mismo tamaño, en
                gris, como ya salen las de Intra y Post». Se va el color, no la barra.

                Llevaban uno por macro -- naranja la proteína, azul los hidratos, amarilla la
                grasa -- y encima se ponían ROJAS al pasarse de 4 g. Ese rojo nunca fue de la
                casa: la regla del color (punto 76) es verde lo resuelto, naranja lo que se
                pasa y nada por debajo, y aquí no la seguía ninguna de las dos cosas. En una
                columna de cinco comidas eso son quince colores que no dicen nada: el estado
                ya lo dice el punto que va al lado del nombre.

                Se queda la LONGITUD, que es lo único que informa: cuánto llevas creado de esa
                comida. El gris es el mismo de «vas por debajo» de lib/estadoDelMacro. */}
            <div className="flex items-center gap-1 mt-2.5">
                {bars.map((b, i) => (
                    <div key={i} className="flex-1 h-1.5 rounded-full bg-muted overflow-hidden">
                        <div className="h-full rounded-full bg-muted-foreground/40 transition-all"
                            style={{ width: `${b.t > 0 ? Math.min((b.v / b.t) * 100, 100) : 0}%` }} />
                    </div>
                ))}
            </div>
        </button>
    );
};

// ===== Fila de comidas (teléfono) =====
// LA FILA C1…Cn (handoff de Nutrición, 16-10). Debajo de lg, una casilla por toma: arriba su
// número y una insignia de estado, abajo su nombre o «Sin crear». Dice de un vistazo cuántas
// hay hechas y cuáles faltan, y sirve para cambiar de comida. Elegir es `onSelect` →
// setSelectedMeal, lo mismo que la columna del ordenador: ni petición ni navegación nuevas.
//
// LA INSIGNIA SIGUE LA REGLA DE COLOR DE SIEMPRE (Enrique, 16-10: «regla actual»): verde lo
// cuadrado, naranja lo que se pasa y sin color lo que falta, que no es un error sino algo por
// hacer. El estado es `getMealStatus` (la misma vara que la tarjeta).
//
// Mismo `data-testid` y mismo `data-vacia` que MealSelectorItem: el recorrido guiado busca
// `meal-select-C*` visibles para abrir la primera comida vacía.
export const MealTabCuadro = ({ mealKey, mealInfo, getMealStatus, isLocked, selected, onSelect, numAlimentos = 0 }) => {
    const info = mealInfo[mealKey];
    const isPeri = mealKey === 'Intra' || mealKey === 'Post';
    const status = getMealStatus(mealKey);
    const vacia = !numAlimentos;
    const ref = React.useRef(null);
    // UN ANCHO MÍNIMO QUE QUEPA «⚡ Intra» CON SU INSIGNIA (16-10): con 64 px, cuatro comidas
    // más el intra y el post se montaban unas sobre otras. Ahora cuatro llenan la fila y, si
    // hay más, la fila se desliza con el dedo.
    // La elegida, siempre a la vista dentro de la fila (sin mover la página en vertical).
    React.useEffect(() => {
        const el = ref.current;
        const fila = el?.parentElement;
        if (!selected || !el || !fila || fila.scrollWidth <= fila.clientWidth) return;
        const izquierda = el.offsetLeft - (fila.clientWidth - el.clientWidth) / 2;
        if (typeof fila.scrollTo === 'function') fila.scrollTo({ left: Math.max(0, izquierda), behavior: 'smooth' });
    }, [selected]);
    const insignia = isLocked
        ? { clase: 'bg-amber-500/20 text-amber-400', dentro: <Lock className="w-2.5 h-2.5" /> }
        : vacia ? { clase: 'border border-white/30 text-muted-foreground', dentro: <Plus className="w-2.5 h-2.5" strokeWidth={3} /> }
        : status === 'cuadrada' ? { clase: 'bg-ok text-[#141414]', dentro: <Check className="w-2.5 h-2.5" strokeWidth={3.5} /> }
        : status === 'sobra' ? { clase: 'bg-pasado text-white', dentro: <span className="text-[10px] font-black leading-none">!</span> }
        : status === 'neutra' ? { clase: 'bg-muted-foreground/40 text-[#141414]', dentro: <Check className="w-2.5 h-2.5" strokeWidth={3.5} /> }
        : { clase: 'bg-foreground/85 text-background', dentro: <span className="text-[10px] font-black leading-none">!</span> };
    return (
        <button ref={ref} type="button" onClick={onSelect} role="tab" aria-selected={selected}
            aria-label={`${info.name}${vacia ? ', sin crear' : ''}`}
            data-testid={`meal-select-${mealKey}`}
            data-vacia={vacia && !isLocked ? 'si' : undefined}
            className={`snap-start flex-1 shrink-0 min-w-[84px] flex flex-col gap-1.5 rounded-[14px] border-[1.5px] px-1.5 pt-2.5 pb-2 text-left transition-colors ${
                selected ? 'bg-muted border-brand' : 'bg-card border-border hover:border-foreground/25'}`}>
            <span className="flex items-center justify-between gap-1">
                <span className={`flex items-center gap-0.5 font-heading text-[17px] font-bold leading-none ${vacia ? 'text-muted-foreground' : 'text-foreground'}`}>
                    {isPeri && <Zap className="w-3 h-3 text-brand" />}
                    <span className="whitespace-nowrap">{isPeri ? info.name : info.shortName}</span>
                </span>
                <span className={`w-4 h-4 rounded-full flex items-center justify-center flex-shrink-0 ${insignia.clase}`}>{insignia.dentro}</span>
            </span>
            <span className={`text-[11px] truncate ${vacia ? 'text-muted-foreground/70' : 'text-muted-foreground'}`}>
                {vacia ? 'Sin crear' : isPeri ? `${numAlimentos} ${numAlimentos === 1 ? 'alimento' : 'alimentos'}` : info.name}
            </span>
        </button>
    );
};

// ===== Tab =====
// Pestañas de verdad: solo el punto de estado y el nombre, y una línea naranja bajo la
// abierta. Las cajas con los macros dentro pesaban tanto como el detalle que abrían.
export const MealTab = ({ mealKey, mealInfo, getMealStatus, isLocked, selected, onSelect }) => {
    const info = mealInfo[mealKey];
    const isPeri = mealKey === 'Intra' || mealKey === 'Post';
    const status = getMealStatus(mealKey);
    return (
        <button onClick={onSelect} data-testid={`meal-tab-${mealKey}`} role="tab" aria-selected={selected}
            className={`flex items-center gap-2 whitespace-nowrap px-3 py-2.5 -mb-px border-b-2 transition-colors ${
                selected
                    ? 'border-brand text-foreground font-semibold'
                    : 'border-transparent text-muted-foreground hover:text-foreground'
            } ${isLocked ? 'opacity-60' : ''}`}>
            <StatusDot status={status} className="flex-shrink-0" />
            {isPeri && <Zap className="w-3.5 h-3.5 flex-shrink-0 text-brand" />}
            <span className="text-sm">{info.name}</span>
            {isLocked && <Lock className="w-3 h-3 flex-shrink-0 text-amber-500" />}
        </button>
    );
};

// ===== Macro progress block =====
/**
 * LOS NÚMEROS DE LA COMIDA, SIEMPRE Y CON EL FORMATO DE LA CASA (puntos 120 a 122 del
 * artifact del 26-08).
 *
 * «Esto ya lo calcula la app. Está escondido detrás de un "ver detalles" en gris.»
 *
 * Y lo estaba de verdad: en el teléfono el bloque nacía cerrado, así que para saber qué le
 * faltaba a la comida había que pedirlo. Lo que enseñaba era además otro formato -- «54/63,5
 * g» con un chip al lado --, distinto del de Inicio y del de la cabecera de esta pantalla.
 * Ahora es el mismo de siempre: número, punto, palabra y barra.
 *
 * AQUÍ SÍ VAN DECIMALES (punto 121). Es la pantalla donde se afina, y el gramo importa: se
 * pide `vista: 'comida'` a lib/estadoDelMacro, que no redondea y que por debajo de 1 g dice
 * «cuadrado» (punto 122). El margen sigue siendo el proporcional de `lib/exceso`, el mismo
 * con el que la comida decide su estado, para no tener dos varas en la misma tarjeta.
 */
const MealProgressBars = ({ mealKey, getMealTarget, calculateMealMacros, hasFoods, neutra = false }) => {
    const target = getMealTarget(mealKey);
    const served = calculateMealMacros(mealKey);
    const isPeri = mealKey === 'Intra' || mealKey === 'Post';

    const claves = isPeri ? ['P', 'H'] : ['P', 'H', 'G'];
    const NOMBRE = { P: 'Proteína', H: 'Hidratos', G: 'Grasa' };

    return (
        <div className="grid grid-cols-3 gap-3" data-testid={`meal-progress-${mealKey}`}>
            {claves.map((k) => {
                // LO QUE FALTA SE CUENTA CONTRA EL OBJETIVO QUE SE ESTÁ VIENDO (17-08-2026).
                // La línea decía «54/63,5 g · faltan 9,3 g», y 54 + 9,3 son 63,3, no 63,5:
                // el objetivo se enseñaba redondeado y el restante salía del valor exacto.
                // Se cuenta contra lo que lee, y desde el 3-09 los dos van a la décima.
                const meta = alDecima(target[k] || 0);
                const tiene = alDecima(served[k] || 0);
                const leida = leerMacro({ vista: 'comida', hay: tiene, objetivo: meta, margen: margenDe(meta) });
                // Comida neutra (el día ya cuadra): «sobran» se dice, pero sin el naranja de
                // «te pasas», que contradiría el «Día listo» de arriba.
                const AVISO = ['pasado', 'sepasa', 'falta'];
                const lectura = neutra
                    ? { ...leida,
                        color: AVISO.includes(leida.color) ? null : leida.color,
                        barra: { ...leida.barra,
                                 color: AVISO.includes(leida.barra?.color) ? null : leida.barra?.color } }
                    : leida;
                return (
                    <div key={k} className="text-center" data-testid={`comida-macro-${mealKey}-${k}`}>
                        <p className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground flex items-center justify-center gap-1">
                            {NOMBRE[k]}
                            {hasFoods && llevaPunto(lectura.color) && (
                                <span className={`w-1.5 h-1.5 rounded-full ${fondoDelMacro(lectura.color)}`} />
                            )}
                        </p>
                        <p className="numero-grande font-data leading-none text-[26px] sm:text-[30px] mt-1.5 text-foreground">
                            {fmt1(tiene)}
                        </p>
                        {/* El objetivo, debajo y pequeño (rediseño del 15-10): con la cifra
                            sola había que subir a la cabecera para saber contra qué se mide. */}
                        <p className="text-xs text-muted-foreground font-data mt-1">de {fmt1(meta)} g</p>
                        {hasFoods && (
                            <p data-testid={`comida-palabra-${mealKey}-${k}`}
                                className={`text-xs mt-1 ${claseDelMacro(lectura.color)}`}>
                                {lectura.palabra}
                            </p>
                        )}
                        {hasFoods && lectura.barra && (
                            <div className="h-1 rounded-full bg-muted mt-1.5 overflow-hidden">
                                <div className={`h-full rounded-full ${fondoDelMacro(lectura.barra.color)}`}
                                    style={{ width: `${lectura.barra.largo}%` }} />
                            </div>
                        )}
                    </div>
                );
            })}
        </div>
    );
};

// ===== Barra fija de macros (teléfono) =====
/**
 * LOS MACROS DE LA COMIDA, SIEMPRE A LA VISTA (rediseño del 15-10). En el teléfono, con una
 * comida de seis u ocho alimentos, los números de arriba se iban de la pantalla en cuanto
 * bajabas a tocar el quinto, y había que subir a ver si ya cuadraba. Esta barra se queda
 * fija encima de la navegación de abajo mientras mueves gramos.
 *
 * Son los MISMOS números que MealProgressBars, leídos igual (getMealTarget,
 * calculateMealMacros y `leerMacro` en vista «comida», con su margen): se repintan solos con
 * cada − / +, porque salen del mismo estado. Ninguna cuenta nueva.
 *
 * Solo debajo de lg: en el ordenador la tarjeta cabe entera y la barra estorbaría.
 * `data-barra-macros` lo usa el layout para subir el botón del chat, que si no la taparía.
 */
// Las columnas de la barra, sueltas: las pinta la barra fija de la comida y también la
// ventana de «Añadir ingrediente» (BuildMealModal), para que las dos digan lo mismo igual.
// `target` y `served` son {P, H, G}: cada pantalla pasa los suyos, ya calculados.
export const FilasDeMacros = ({ target, served, isPeri = false, hasFoods = true, neutra = false, testId, conPalabra = false }) => {
    const claves = isPeri ? ['P', 'H'] : ['P', 'H', 'G'];
    const NOMBRE = { P: 'Proteína', H: 'Hidratos', G: 'Grasa' };
    const AVISO = ['pasado', 'sepasa', 'falta'];
    return (
        <div className={`grid ${isPeri ? 'grid-cols-2' : 'grid-cols-3'} gap-3 px-4 py-2.5`}>
            {claves.map((k) => {
                const meta = alDecima(target?.[k] || 0);
                const tiene = alDecima(served?.[k] || 0);
                const leida = leerMacro({ vista: 'comida', hay: tiene, objetivo: meta, margen: margenDe(meta) });
                const color = neutra && AVISO.includes(leida.color) ? null : leida.color;
                const barra = neutra && AVISO.includes(leida.barra?.color) ? { ...leida.barra, color: null } : leida.barra;
                return (
                    <div key={k} className="min-w-0" data-testid={testId ? `${testId}-${k}` : undefined}>
                        <p className="flex items-center gap-1 text-[11px] font-bold uppercase tracking-wide text-muted-foreground">
                            {NOMBRE[k]}
                            {hasFoods && llevaPunto(color) && <span className={`w-1.5 h-1.5 rounded-full ${fondoDelMacro(color)}`} />}
                        </p>
                        <p className="font-data whitespace-nowrap leading-tight mt-0.5">
                            <span className="text-base font-bold text-foreground">{fmt1(tiene)}</span>
                            <span className="text-xs text-muted-foreground"> / {fmt1(meta)} g</span>
                        </p>
                        <div className="h-1.5 rounded-full bg-muted mt-1.5 overflow-hidden">
                            {hasFoods && barra && (
                                <div className={`h-full rounded-full transition-all duration-200 ${fondoDelMacro(barra.color)}`}
                                    style={{ width: `${barra.largo}%` }} />
                            )}
                        </div>
                        {/* La palabra de siempre («válido −3,2 g», «cuadrado», «faltan 12 g»), en el
                            color de la regla de siempre. */}
                        {conPalabra && (
                            <p className={`text-[11px] font-semibold mt-1 truncate ${hasFoods ? claseDelMacro(color) : 'text-muted-foreground'}`}>
                                {hasFoods ? leida.palabra : '—'}
                            </p>
                        )}
                    </div>
                );
            })}
        </div>
    );
};

// CON EL NOMBRE DE LA COMIDA (handoff, 16-10): «C1 · DESAYUNO» y, a la derecha, cómo va.
// Sin él la barra decía «53 / 56,2» debajo de una tarjeta que decía «53 / 225» y parecía un
// error: siempre es la comida abierta, nunca el día.
const BarraMacrosFija = ({ mealKey, getMealTarget, calculateMealMacros, hasFoods, neutra = false, nombre, status }) => {
    const resumen = !hasFoods ? { texto: 'Sin alimentos', clase: 'text-muted-foreground' }
        : status === 'cuadrada' ? { texto: 'Comida cuadrada', clase: 'text-ok' }
        : neutra ? { texto: 'Día cuadrado', clase: 'text-muted-foreground' }
        : status === 'sobra' ? { texto: 'Ajusta cantidades', clase: 'text-pasado' }
        : { texto: 'Ajusta cantidades', clase: 'text-foreground' };
    return (
        <div className="lg:hidden fixed left-0 right-0 bottom-16 z-40 border-t border-border bg-card/95 backdrop-blur supports-[backdrop-filter]:bg-card/85 shadow-[0_-8px_24px_rgba(0,0,0,0.35)]"
            data-barra-macros="" data-testid={`barra-macros-${mealKey}`} aria-label="Macros de la comida">
            <div className="flex items-baseline justify-between gap-3 px-4 pt-2.5 text-xs">
                <span className="font-bold uppercase tracking-wider text-muted-foreground truncate">{nombre}</span>
                <span className={`font-semibold flex-shrink-0 ${resumen.clase}`} data-testid={`barra-resumen-${mealKey}`}>{resumen.texto}</span>
            </div>
            <FilasDeMacros target={getMealTarget(mealKey)} served={calculateMealMacros(mealKey)}
                isPeri={mealKey === 'Intra' || mealKey === 'Post'} hasFoods={hasFoods} neutra={neutra}
                testId={`barra-macro-${mealKey}`} conPalabra />
        </div>
    );
};

// ===== Comida vacía =====
const MENOS = '−';
const diaDeLaTarjeta = (fecha) => {
    const hoy = new Date(`${ahoraDeLaApp().toLocaleDateString('en-CA')}T12:00:00`);
    const d = new Date(`${fecha}T12:00:00`);
    const n = Math.round((hoy - d) / 864e5);
    if (n === 1) return 'Ayer';
    if (n === 2) return 'Anteayer';
    const texto = d.toLocaleDateString('es-ES', { weekday: 'long', day: 'numeric' });
    return d.getMonth() === hoy.getMonth() ? texto
        : `${texto} ${d.toLocaleDateString('es-ES', { month: 'short' }).replace('.', '')}`;
};

// Cómo encaja una comida de otro día con el objetivo de ESTA: «Cuadra» si los tres macros caen
// dentro del margen de siempre (`margenDe`, 4 g); si no, la desviación que más pesa («−8 P»).
// Con la regla de color de siempre: verde lo que cuadra, naranja lo que sobra, sin color lo
// que falta. Es solo un aviso: al tocarla, se copia cuadrada a tu objetivo.
const encajeDe = (macros, objetivo, esPeri) => {
    if (!macros || !objetivo) return null;
    const claves = esPeri ? ['P', 'H'] : ['P', 'H', 'G'];
    const desvios = claves.map((k) => ({ k, d: (macros[k] || 0) - (objetivo[k] || 0) }));
    if (desvios.every(({ k, d }) => Math.abs(d) <= margenDe(objetivo[k] || 0))) {
        return { texto: 'Cuadra', clase: 'text-ok' };
    }
    const peor = desvios.reduce((a, b) => (Math.abs(b.d) > Math.abs(a.d) ? b : a));
    return {
        texto: `${peor.d > 0 ? '+' : MENOS}${Math.round(Math.abs(peor.d))} ${peor.k}`,
        clase: peor.d > 0 ? 'text-pasado' : 'text-foreground/80',
    };
};

const lineaDeMacros = (m) => (m
    ? `${Math.round(m.P || 0)} P · ${Math.round(m.H || 0)} H · ${Math.round(m.G || 0)} G` : '');

/**
 * LA COMIDA SIN ALIMENTOS (handoff «Crear comida», 16-10). Los cuatro botones iguales pasan a
 * tener jerarquía: arriba las dos maneras de crearla, al mismo nivel (que te la sugieran o
 * elegir tú); debajo, traerla de otro día o de tus favoritas, ya con lo que traen a la vista.
 *
 * Cada cosa abre el flujo de siempre y ninguno cambia:
 *   Sugiéreme un menú → loadMenuOptions        Elijo yo → «Añadir ingrediente» (BuildMealModal)
 *   una tarjeta de otro día → copyMealFromDay (cuadrada a tu objetivo), «Ver más» → su modal
 *   el «+» de una favorita → aplicarComidaFavorita,  «Ver todas» → FavoritasDeComida
 * Las dos secciones de abajo salen siempre (16-10): sin nada que enseñar, lo dicen.
 */
const ComidaVacia = ({ mealKey, nombre, objetivo, sangria, conRaya = true, loadMenuOptions, setBuildMealModal,
    openRepeatModal, recientes = [], onRepetir, favoritas = [], onAplicarFavorita, abrirFavoritas,
    // EL POSTENTRENO, CON LA MISMA CARA (Enrique, 16-10). `sugerir` cambia la tarjeta naranja
    // ({ titulo, texto, onClick, testId, ocupado }; `null` = no hay nada que sugerir), `modo`
    // el de «Elijo yo», y `tituloFavoritas` el rótulo de abajo.
    sugerir, modo = 'normal', tituloFavoritas = 'Mis comidas favoritas' }) => {
    const titulo = 'text-[13px] font-bold uppercase tracking-wider text-muted-foreground';
    const tarjetaNaranja = sugerir === undefined
        ? { titulo: 'Sugiéreme un menú', texto: 'Te proponemos alimentos y cantidades que cuadran tus macros.',
            onClick: () => loadMenuOptions(mealKey), testId: `menu-options-${mealKey}` }
        : sugerir;
    return (
        <div className={`space-y-3 ${conRaya ? '' : 'max-lg:!mt-0'}`} data-testid={`comida-vacia-${mealKey}`}>
            {conRaya && <div className={`h-px bg-border ${sangria}`} aria-hidden="true" />}
            <p className={titulo}>Crea tu {nombre}</p>
            <div className={`grid gap-2 ${tarjetaNaranja ? 'grid-cols-2' : 'grid-cols-1'}`}>
                {tarjetaNaranja && (
                <button type="button" onClick={tarjetaNaranja.onClick} data-testid={tarjetaNaranja.testId} disabled={tarjetaNaranja.ocupado}
                    className="min-h-[132px] rounded-2xl bg-brand text-[#141414] p-4 flex flex-col gap-2 text-left hover:brightness-105 transition disabled:opacity-60">
                    <span className="font-heading text-[25px] font-extrabold leading-none">{tarjetaNaranja.titulo}</span>
                    <span className="text-[13px] font-medium leading-snug flex-1">{tarjetaNaranja.texto}</span>
                    <span className="w-8 h-8 rounded-full bg-[#141414] text-brand flex items-center justify-center self-end">
                        <ArrowRight className="w-4 h-4" strokeWidth={2.75} />
                    </span>
                </button>
                )}
                <button type="button" onClick={() => setBuildMealModal({ open: true, mealKey, mode: modo })} data-testid={`build-meal-${mealKey}`}
                    className="min-h-[132px] rounded-2xl bg-muted border-[1.5px] border-white/15 text-foreground p-4 flex flex-col gap-2 text-left hover:border-white/30 transition">
                    <span className="font-heading text-[25px] font-extrabold leading-none">Elijo yo</span>
                    <span className="text-[13px] font-medium leading-snug text-muted-foreground flex-1">Elige varios alimentos y ajusta la cantidad de cada uno.</span>
                    <span className="w-8 h-8 rounded-full bg-foreground text-background flex items-center justify-center self-end">
                        <ArrowRight className="w-4 h-4" strokeWidth={2.75} />
                    </span>
                </button>
            </div>

            {/* SIEMPRE, AUNQUE NO HAYA NINGUNA (Enrique, 16-10, con el postentreno: «echo en falta
                Repetir de otro día»). Como las favoritas: sin recientes se dice, y «Ver más» sigue
                abriendo la ventana, que busca más atrás que estas tarjetas. */}
            {openRepeatModal && (
                <div className="space-y-2 pt-1.5" data-testid={`repetir-de-otro-dia-${mealKey}`}>
                    <div className="flex items-baseline justify-between gap-3">
                        <p className={titulo}>Repetir de otro día</p>
                        <button type="button" onClick={() => openRepeatModal(mealKey)} data-testid={`repeat-meal-${mealKey}`}
                            className="text-[13px] font-semibold text-brand hover:brightness-110 min-h-[32px]">Ver más</button>
                    </div>
                    {recientes.length === 0 ? (
                        <p className="rounded-[14px] bg-muted border border-white/10 px-3.5 py-3 text-sm text-muted-foreground"
                            data-testid={`sin-recientes-${mealKey}`}>
                            {modo === 'post' ? 'No lo tienes en los últimos días' : 'No la tienes en los últimos días'}
                        </p>
                    ) : (
                    <div className={`flex gap-2 overflow-x-auto snap-x [scrollbar-width:none] [&::-webkit-scrollbar]:hidden ${sangria} ${
                        // Escritas enteras: Tailwind solo genera las clases que lee tal cual.
                        conRaya ? 'px-3.5 sm:px-4 scroll-px-3.5 sm:scroll-px-4' : 'px-4 sm:px-5 scroll-px-4 sm:scroll-px-5'}`}>
                        {recientes.map((r) => {
                            const encaje = encajeDe(r.macros, objetivo, false);
                            return (
                                <button key={r.fecha} type="button" onClick={() => onRepetir(mealKey, r.fecha)}
                                    data-testid={`repetir-${mealKey}-${r.fecha}`}
                                    aria-label={`Repetir la comida del ${diaDeLaTarjeta(r.fecha)}`}
                                    className="snap-start flex-none w-[210px] rounded-[14px] bg-muted border border-white/10 p-3 flex flex-col gap-1.5 text-left hover:border-white/25 transition-colors">
                                    <span className="flex items-center justify-between gap-2">
                                        <span className="text-xs font-bold uppercase text-muted-foreground truncate">{diaDeLaTarjeta(r.fecha)}</span>
                                        {encaje && <span className={`text-[11px] font-bold flex-shrink-0 ${encaje.clase}`}>{encaje.texto}</span>}
                                    </span>
                                    <span className="text-sm font-semibold leading-snug line-clamp-2">{r.alimentos.join(', ')}</span>
                                    {r.macros && <span className="text-xs text-muted-foreground font-data">{lineaDeMacros(r.macros)}</span>}
                                </button>
                            );
                        })}
                        <span className="flex-none w-px" aria-hidden="true" />
                    </div>
                    )}
                </div>
            )}

            {/* SIEMPRE, AUNQUE NO HAYA NINGUNA (Enrique, 16-10): sin favoritas se dice, para que
                se sepa que existen. «Ver todas» solo cuando hay algo que ver. */}
            {abrirFavoritas && (
                <div className="space-y-2 pt-1.5" data-testid={`favoritas-de-la-comida-${mealKey}`}>
                    <div className="flex items-baseline justify-between gap-3">
                        <p className={titulo}>{tituloFavoritas}</p>
                        {favoritas.length > 0 && (
                            <button type="button" onClick={() => abrirFavoritas(mealKey)} data-testid={`fav-comida-vacia-${mealKey}`}
                                className="text-[13px] font-semibold text-brand hover:brightness-110 min-h-[32px]">Ver todas ({favoritas.length})</button>
                        )}
                    </div>
                    {favoritas.length === 0 ? (
                        <p className="rounded-[14px] bg-muted border border-white/10 px-3.5 py-3 text-sm text-muted-foreground"
                            data-testid={`sin-favoritas-${mealKey}`}>
                            {modo === 'post' ? 'Aún no has guardado ninguno' : 'Aún no has guardado ninguna'}
                        </p>
                    ) : (
                    <div className="rounded-[14px] bg-muted border border-white/10 divide-y divide-white/10">
                        {favoritas.slice(0, 2).map((f) => (
                            <div key={f.id || f.name} className="flex items-center gap-2.5 py-2.5 pl-3.5 pr-2.5">
                                <Star className="w-4 h-4 text-brand flex-shrink-0" fill="currentColor" />
                                <div className="min-w-0 flex-1">
                                    <p className="text-[15px] font-semibold leading-tight break-words">{f.name}</p>
                                    <p className="text-xs text-muted-foreground truncate mt-0.5">
                                        {(f.alimentos || []).map((a) => a.nombre).filter(Boolean).join(', ')}
                                    </p>
                                    {f.macros_snapshot && (
                                        <p className="text-xs text-muted-foreground/80 font-data">{lineaDeMacros(f.macros_snapshot)}</p>
                                    )}
                                </div>
                                <button type="button" onClick={() => onAplicarFavorita(f, mealKey)}
                                    aria-label={`Poner «${f.name}» en esta comida`} data-testid={`fav-poner-${mealKey}-${f.id || f.name}`}
                                    className="w-11 h-11 rounded-xl bg-white/10 text-foreground flex items-center justify-center flex-shrink-0 hover:bg-brand hover:text-white transition-colors">
                                    <Plus className="w-5 h-5" />
                                </button>
                            </div>
                        ))}
                    </div>
                    )}
                </div>
            )}
        </div>
    );
};

// ===== Ingredient row =====
const IngredientRow = ({ food, idx, mealKey, isLocked, isEditing, increment,
    moveFoodUp, removeFood, updateFoodQuantity, updateFoodQuantityDirect,
    setEditingQuantity, formatFoodQuantity,
    esPorUnidad, pesoUnidad, acumFamilias, ordenando = false }) => {
    // Los del método, que son los que la app cuenta. Hasta el 26-08 aquí se podía
    // enseñar en su lugar lo que dice la etiqueta (Método / Reales), y hacía falta un
    // aviso al principio de la pantalla explicando que lo que se veía no era lo que
    // contaba. Punto 112: fuera las dos cosas.
    const macros = food?.macros_efectivos || {};
    // Los alimentos por unidades se escriben en unidades ("2 huevos"), no en gramos.
    const porUnidad = esPorUnidad ? esPorUnidad(food) : false;
    const peso = pesoUnidad ? pesoUnidad(food) : 100;
    const valorEditable = porUnidad
        ? unidadesParaVer(food.cantidad_g || 0, peso)
        : (food.cantidad_g || 0);
    // EL − SE VUELVE × EN EL MÍNIMO (handoff, 16-10): el siguiente toque ya no baja, quita
    // (el mismo `updateFoodQuantity`, que pregunta antes, como siempre). Así se ve venir, y
    // la papelera deja de hacer falta en el teléfono. El mínimo es el de NutritionPage
    // (`cantidadMinima`): una unidad entera en los de unidades (media si la admite) y 1 g en el resto.
    const enElMinimo = (food.cantidad_g || 0) - increment < (porUnidad ? minimoPorUnidad(food, peso) : 1);
    return (
        // LA FILA COMPACTA (maqueta del 15-10): una sola línea en todos los anchos. A la
        // izquierda el nombre y, debajo, sus macros (y el contador de su familia si lo
        // tiene); a la derecha, la cantidad y la papelera, siempre en el mismo sitio. Antes,
        // por debajo de 720 px de fila -- casi siempre --, el nombre iba en su línea, los
        // macros en otra y los gramos en una tercera: tres alturas por alimento y huecos
        // vacíos a los lados. El nombre se parte en dos líneas como mucho, nunca la fila.
        // UNA LÍNEA TAMBIÉN EN EL TELÉFONO (handoff, 16-10): sin la papelera, el stepper ocupa
        // ~140 px y al nombre le queda el resto; se parte en las líneas que haga falta, entero.
        // A la izquierda nombre y lo que aporta; a la derecha la cantidad.
        // Y SI NO CABEN LOS DOS, LA CANTIDAD BAJA (Enrique, 16-10, con «Leche de almendras sin
        // azúcares añadidos» y «al gusto (200 ml)»: el nombre quedaba en una columna de cinco
        // letras). El nombre pide 9rem como poco; con una cantidad corta cabe todo en una
        // línea, como siempre, y con una larga la cantidad pasa debajo, a la derecha.
        <div className="flex flex-wrap items-center gap-2 rounded-xl border border-border bg-muted/40 px-3 py-2.5 sm:px-2.5 sm:py-2" data-testid={`fila-${mealKey}-${idx}`}>
            {/* SUBIR, SOLO MIENTRAS SE ORDENA (punto 126), y sin la palabra «prioridad»
                (punto 127): solo coloca el alimento más arriba. */}
            {ordenando && (
                <button
                    className="flex items-center justify-center h-9 w-7 rounded-lg text-muted-foreground hover:text-brand hover:bg-brand/10 disabled:opacity-25 disabled:hover:bg-transparent disabled:cursor-not-allowed transition-colors flex-shrink-0"
                    disabled={idx === 0 || isLocked} onClick={() => moveFoodUp(mealKey, idx)}
                    title={idx === 0 ? 'Ya está el primero' : 'Subirlo'}
                    data-testid={`reorder-${mealKey}-${idx}`}
                >
                    <ArrowUp className="w-4 h-4" strokeWidth={2.5} />
                </button>
            )}

            <div className="min-w-0 flex-1 basis-36">
                {/* Los alimentos de marca tienen ficha propia: el nombre abre su enlace
                    (naranja = tiene ficha). Dos líneas como mucho (punto 12 del 23-08). */}
                {food.url ? (
                    <a href={food.url} target="_blank" rel="noopener noreferrer"
                        className="text-[15px] lg:text-sm font-semibold leading-snug text-brand underline underline-offset-2 break-words sm:line-clamp-2"
                        title={`${food.nombre} (abre la ficha del producto)`}>
                        {food.nombre}
                    </a>
                ) : (
                    <p className="text-[15px] lg:text-sm font-semibold leading-snug text-foreground break-words sm:line-clamp-2" title={food.nombre}>{food.nombre}</p>
                )}
                {/* Macros con palabras, enteros (ficha 27): si no caben, bajan de línea. */}
                <p className="text-[13px] lg:text-xs text-muted-foreground font-data mt-0.5"
                    title={macrosLine(macros)} data-testid={`macros-${mealKey}-${idx}`}>{macrosLine(macros)}</p>
                {/* Lo que llevas hoy de su familia, si es de las que se calibran (Jesús,
                    13-08: «un contador en la línea del alimento desde el primer gramo»). */}
                <ContadorFamilia bloque={food.bloque} gramos={acumFamilias?.[food.bloque]?.gramos}
                    proteinaCuenta={food.proteina_cuenta !== false}
                    proteinaCrece={food.proteina_crece !== false}
                    className="mt-1" />
            </div>

            {/* Cantidad y papelera juntas, siempre a la derecha (Francisco, 26-09: «la gente
                da a los botones por memoria muscular»). */}
            <div className="flex items-center gap-1.5 sm:gap-1 flex-shrink-0 ml-auto">
            <div className="inline-flex items-stretch h-11 sm:h-9 rounded-lg border border-border bg-card overflow-hidden flex-shrink-0"
                title={porUnidad ? `Cantidad en unidades · 1 ud = ${num1(peso)} g` : 'Cantidad en gramos'}>
                <button className={`w-10 sm:w-auto sm:px-2 flex items-center justify-center hover:bg-brand hover:text-white disabled:opacity-40 disabled:hover:bg-transparent disabled:hover:text-foreground transition-colors ${enElMinimo ? 'text-red-400' : 'text-foreground'}`} disabled={isLocked} onClick={() => updateFoodQuantity(mealKey, idx, -increment)}
                    aria-label={enElMinimo ? 'Quitar alimento' : porUnidad ? 'Menos unidades' : 'Menos gramos'}
                    data-testid={`menos-${mealKey}-${idx}`}>
                    {enElMinimo ? <X className="w-4 h-4 sm:w-3.5 sm:h-3.5" strokeWidth={2.75} /> : <Minus className="w-4 h-4 sm:w-3.5 sm:h-3.5" />}
                </button>
                {isEditing ? (
                    // Con techo: el campo no tenía `max`, así que la flecha de subir no
                    // paraba nunca. El techo es el tope duro, en la unidad del campo; lo que
                    // es mucho para ESTE alimento se avisa aparte, sin cortar.
                    <input type="number" defaultValue={valorEditable} autoFocus
                        step={pasoDelCampo(food, porUnidad)} min="0"
                        aria-label={porUnidad ? 'Unidades' : 'Gramos'}
                        className="w-[56px] sm:w-14 text-center text-base sm:text-sm font-bold font-data bg-transparent border-x border-border text-foreground focus:outline-none"
                        onBlur={(e) => updateFoodQuantityDirect(mealKey, idx, e.target.value)}
                        onKeyDown={(e) => { if (e.key === 'Enter') updateFoodQuantityDirect(mealKey, idx, e.target.value); if (e.key === 'Escape') setEditingQuantity({ mealKey: null, foodIndex: null }); }} />
                ) : (
                    <button className="min-w-[56px] px-2 font-heading text-lg sm:font-data sm:text-sm font-bold text-center text-foreground border-x border-border hover:text-brand disabled:opacity-50 transition-colors whitespace-nowrap" disabled={isLocked}
                        onClick={() => !isLocked && setEditingQuantity({ mealKey, foodIndex: idx })} data-testid={`qty-${mealKey}-${idx}`}>
                        {formatFoodQuantity ? formatFoodQuantity(food) : `${num1(food.cantidad_g || 0)} g`}
                        {/* A CUÁNTO EQUIVALE, EN TOTAL (Jesús, 15-08, fallo 46; 16-08): «5 ud
                            (50 g)», lo que se come, no lo que pesa una. */}
                        {porUnidad && (
                            <span className="ml-1 text-[10px] font-normal text-muted-foreground">({num1(Math.round(food.cantidad_g || 0))} g)</span>
                        )}
                    </button>
                )}
                <button className="w-10 sm:w-auto sm:px-2 flex items-center justify-center text-foreground hover:bg-brand hover:text-white disabled:opacity-40 disabled:hover:bg-transparent disabled:hover:text-foreground transition-colors" disabled={isLocked} onClick={() => updateFoodQuantity(mealKey, idx, increment)}
                    aria-label={porUnidad ? 'Más unidades' : 'Más gramos'}>
                    <Plus className="w-4 h-4 sm:w-3.5 sm:h-3.5" />
                </button>
            </div>

            <button className="hidden sm:flex h-9 w-8 rounded-lg items-center justify-center text-muted-foreground hover:text-red-500 hover:bg-red-500/10 disabled:opacity-30 transition-colors flex-shrink-0" disabled={isLocked} onClick={() => removeFood(mealKey, idx)} aria-label="Eliminar alimento" data-testid={`remove-${mealKey}-${idx}`}>
                <Trash2 className="w-4 h-4" />
            </button>
            </div>
        </div>
    );
};

// ===== Meal card =====
const MealCard = ({
    mealKey, mealInfo, mealsData, expandedMeals, setExpandedMeals,
    // Los suplementos que le tocan con esta comida (maqueta de la parte 6). Solo los
    // nombres: la dosis vive en su pantalla. El intra y el post no traen nunca.
    suplementos, irASuplementos = null,
    getMealTarget, calculateMealMacros, getMealStatus,
    loadMenuOptions, setBuildMealModal, openRepeatModal,
    removeFood, moveFoodUp, updateFoodQuantity, updateFoodQuantityDirect,
    editingQuantity, setEditingQuantity, getQuantityIncrement,
    clearMeal, formatFoodQuantity, esPorUnidad, pesoUnidad,
    isLocked = false, onCuadrar,
    mealMode = 'auto', setMealMode, forceExpanded = false, denso = false,
    // Solo cambia lo que pone en la LISTA de ingredientes. Los totales de la comida,
    // su estado y las cantidades siguen siendo del metodo, pase lo que pase.
    // Lo que lleva el DIA de cada familia que se calibra, para el contador de la linea del
    // alimento. Viene de `mealCardProps` y es el mismo para todas las comidas: desde el
    // 13-08 el tramo lo decide el total del dia, no la comida (ver `ContadorFamilia`).
    acumFamilias = null,
    // Abre las favoritas DE ESTA COMIDA (25-08). Si no llega, los botones no se pintan:
    // la tarjeta se usa tambien desde sitios que no tienen ese modal.
    abrirFavoritasDeComida = null,
    // La comida vacía: lo que trae «Repetir de otro día» y «Mis comidas favoritas» (16-10).
    recientesDeLaComida = null, repetirDeOtroDia = null, favoritasDeComida = [], aplicarFavoritaEn = null,
    // Su postentreno de siempre (`profile.peri_favorito`): sale delante en los favoritos del post.
    periFavorito = null,
    // «Crear intra» / «Crear post» (25-09): monta el peri de un toque. `creandoPeri` es el
    // mealKey que se está creando, para no pulsarlo dos veces. Sin la función no se pinta.
    crearPeri = null, creandoPeri = null,
    // LA VUELTA ATRAS DE LO ULTIMO QUE SE HIZO EN ESTA COMIDA (Jesus, 6-09-2026).
    // `{ texto, onDeshacer }` o `null`. Lo decide quien monta la tarjeta (NutritionPage):
    // aqui solo se pinta. Su frase: «cuando aparece el boton de deshacer en las
    // notificaciones no se puede tocar; podriamos ponerlo en la pantalla de la comida donde
    // hace la modificacion, como quitar o algo». El aviso de abajo a la derecha se iba solo
    // a los ocho segundos y en el telefono ese rato se va en leerlo.
    deshacer = null,
    // «NO QUIERO INTRA» (Jesús, «El intra, paso a paso», 27-09): hoy sin intra; sus macros van
    // a las demás comidas. Sin la función no se pinta.
    // «Sugiéreme un postentreno» (Jesús, «El postentreno, cerrado», 27-09).
    sugerirPost = null,
}) => {
    // ORDENANDO (punto 126): las flechas de subir solo salen cuando se pide.
    const [ordenando, setOrdenando] = React.useState(false);
    const isExpanded = forceExpanded ? true : expandedMeals[mealKey];
    const target = getMealTarget(mealKey);
    const foods = mealsData[mealKey]?.alimentos || [];
    // Aquí sobraba un macro, había varios sitios de donde bajarlo y se bajó de todos a la
    // vez porque nadie pudo preguntar (ver el aviso de más abajo, 31-08-2026).
    const eleccionPendiente = Boolean(mealsData[mealKey]?.eleccion_pendiente);
    // Y el caso duro (3-09-2026): la pregunta pendiente es de QUITAR, porque ni con todo
    // al mínimo cabe. Ahí no se bajó nada -- no había nada que bajar -- y decir «lo bajé
    // de todo a la vez» era mentira: la C4 de Francisco, con los mínimos del yogur, las
    // fresas y el skyr sumando 18 H sobre un objetivo de 10.
    const pendienteEsQuitar = mealsData[mealKey]?.eleccion_pendiente?.decision?.tipo === 'quitar';
    // QUÉ SOBRA Y CUÁNTO, QUE SI NO NO SE ENTIENDE (Francisco, 21-09): «aquí sobra, ¿qué?
    // No se entiende, debe aclarar qué sobra y cuánto». La decisión ya trae las dos cosas
    // -- el macro y los gramos --; el cartel las tenía a mano y no las decía.
    const loQueSobraAqui = (() => {
        const d = mealsData[mealKey]?.eleccion_pendiente?.decision;
        if (!d || !d.macro) return null;
        const cuanto = Number(d.sobra);
        if (!Number.isFinite(cuanto) || cuanto <= 0) return null;
        return `${num1(cuanto)} g de ${NOMBRE_MACRO[d.macro] || d.macro}`;
    })();
    const isPeri = mealKey === 'Intra' || mealKey === 'Post';
    const info = mealInfo[mealKey];
    const status = getMealStatus(mealKey);
    // En qué punto está la comida, con su frase. Se calcula aquí arriba porque el titular
    // del teléfono depende de ella: cuando la comida se pasa, el texto es largo («Te pasas
    // 29 g de hidratos y 12 g de grasa») y OCUPA EL SITIO DEL NOMBRE en vez de pelearse con
    // él. Francisco, 13-08: «ese texto tapa el título de Comida 1, Comida 2... haz que lo
    // reemplace si se pasa». Los otros estados son cortos («Cuadrada», «Te falta») y caben al
    // lado, así que esos no se tocan.
    const estado = estadoDeLaComida(status, target, calculateMealMacros(mealKey), foods.length, isPeri, isLocked);

    // CON LETRA Y CON EL MEDIO GRAMO: «52,5P · 10H · 15G» (maqueta de la parte 6, y la
    // decisión del 29-08 dentro del punto 115).
    //
    // El 115 quitó las dos cosas -- «53 · 10 · 15» --; las letras volvieron con la parte 6, y
    // el decimal vuelve ahora: «cuando el objetivo cae en medio gramo, se escribe -- 52,5P
    // ·10H · 15G, nunca 53,0P». Aquí ponía `num0`, que redondea a entero, y este comentario
    // decía que el documento no tocaba los decimales: lo tocó el 29.
    //
    // Y NO ES COSMÉTICA, son dos cuentas que no cuadraban. Con 53 escrito, los seis objetivos
    // del día suman 176 sobre un día de 175; y el «faltan 11,2» de debajo está restado contra
    // un 52,5 que no aparecía en ninguna parte, así que la resta que hace el cliente no le
    // daba.
    //
    // A LA DÉCIMA Y NO AL MEDIO GRAMO (Francisco, 3-09). Era `numMedio`, y con lo servido a
    // la décima el mismo objetivo se leía «32,5» aquí y «32,6» en Inicio. `num1` sigue sin
    // escribir «53,0»: los enteros salen enteros.
    //
    // Y CON SU LETRA (Francisco, 8-09-2026): «Objetivo · 33P · 20H · 10G», en el orden de
    // siempre, proteína · hidratos · grasa. Se quitaron el 3-09 por las fichas 26 y 27 del
    // parte de Gonzalo, con el argumento de que el orden ya lo dice; vuelven todas.
    // La grasa del peri no cuenta en el método, así que ahí son dos números.
    const lineaObjetivo = isPeri
        ? `${num1(target.P)}P · ${num1(target.H)}H`
        : `${num1(target.P)}P · ${num1(target.H)}H · ${num1(target.G)}G`;

    // SE APAGA LO HECHO, EN VEZ DE PINTAR LO QUE FALTA (punto 197 del 27-08).
    //
    // El 117 pedía «lo que te pide algo se ve, lo que ya está se apaga», y eso se queda: lo que
    // cambia es cómo se consigue. La comida sin crear iba en NARANJA entera, borde y fondo, y
    // eso gastaba el único color que la app tiene reservado para lo que se pasa (punto 76).
    // Ahora sólo se baja la opacidad de la que ya está cuadrada: mismo efecto, sin color.
    //
    // Y sale mejor de lo que pedía el 117: abres Nutrición y lo que se ve es lo que te queda
    // por hacer. En un día a medias no hay ni un color en pantalla, porque no hay nada que
    // corregir -- sólo cosas que hacer.
    const yaEsta = !isLocked && estado.color === 'ok';

    // ── LA PUERTA DEL AUTOAJUSTE (Jesús, doc 21-08, apartado 14) ─────────────────────
    //
    // En Automático, tocar una cantidad puede hacer que la app la cambie sola: el cruce
    // de tramo de una familia calibrada (doc 57, F3) recuadra la comida y deja, por
    // ejemplo, las almendras en 20 g cuando el cliente escribió 40. El toast decía QUE
    // se hizo, pero no DONDE se decide lo que él quería. Aquí se apunta la cantidad
    // pedida al tocar; si después la comida vuelve con otra cantidad en ese alimento,
    // sale un recuadro pegado al ingrediente que SE QUEDA hasta que el cliente decida.
    // El botón hace las DOS cosas: pasa la comida a Manual y repone su cantidad.
    //
    // El reajuste en sí vive en NutritionPage (anotarCalibracion -> cuadrarComida
    // silencioso); esta tarjeta es quien recibe el gesto y quien pinta el ingrediente,
    // así que la puerta se monta aquí sin tocar el cálculo ni el reparto.
    const idDe = (f) => f?.alimento_id ?? f?.id ?? f?._id ?? f?.nombre;
    const [ajusteAuto, setAjusteAuto] = React.useState(null);   // { foodId, pedida, ajustada } en gramos
    const peticionPendiente = React.useRef(null);               // { foodId, gramos, confirmada }

    // La cantidad como la lee el cliente: en unidades si el alimento va por unidades.
    const fmtCant = (food, g) => {
        if (esPorUnidad && esPorUnidad(food)) {
            const peso = (pesoUnidad ? pesoUnidad(food) : 100) || 100;
            return `${num1(unidadesParaVer(g, peso))} ud (${num1(g)} g)`;
        }
        return `${num1(g)} g`;
    };

    // Se apunta lo pedido. Si toca otra cantidad antes de decidir, el recuadro pasa al
    // ingrediente nuevo (se limpia aquí y, si el nuevo gesto también se ajusta, vuelve
    // a salir en él). En Manual no hay autoajuste, así que no hay nada que vigilar.
    const anotarPeticion = (food, gramosPedidos) => {
        setAjusteAuto(null);
        if (mealMode === 'manual' || isPeri || isLocked || !setMealMode || !(gramosPedidos > 0)) {
            peticionPendiente.current = null;
            return;
        }
        peticionPendiente.current = { foodId: idDe(food), gramos: Math.round(gramosPedidos), confirmada: false };
    };

    const updateFoodQuantityVigilada = (mk, idx, delta) => {
        const f = foods[idx];
        if (f) anotarPeticion(f, (f.cantidad_g || 0) + delta);
        updateFoodQuantity(mk, idx, delta);
    };

    const updateFoodQuantityDirectVigilada = (mk, idx, valor) => {
        const f = foods[idx];
        if (f) {
            const n = parseFloat(String(valor).replace(',', '.'));
            const gramos = Number.isFinite(n)
                ? (esPorUnidad && esPorUnidad(f) ? n * ((pesoUnidad ? pesoUnidad(f) : 100) || 100) : n)
                : 0;
            anotarPeticion(f, gramos);
        }
        updateFoodQuantityDirect(mk, idx, valor);
    };

    // La vigilancia: primero hay que VER la cantidad pedida puesta (si no entró tal
    // cual -- tope, mínimo, valor rechazado -- no hay autoajuste que contar); si después
    // cambia sin que el cliente la haya tocado, ese es el reajuste y sale el recuadro.
    React.useEffect(() => {
        const p = peticionPendiente.current;
        if (!p) return;
        const f = foods.find(x => idDe(x) === p.foodId);
        if (!f) { peticionPendiente.current = null; return; }
        const q = Math.round(f.cantidad_g || 0);
        if (!p.confirmada) {
            if (Math.abs(q - p.gramos) < 1) p.confirmada = true;
            else peticionPendiente.current = null;
            return;
        }
        if (Math.abs(q - p.gramos) >= 1) {
            setAjusteAuto({ foodId: p.foodId, pedida: p.gramos, ajustada: q });
            peticionPendiente.current = null;
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps -- se vigila la lista de alimentos
    }, [foods]);

    // El cliente quiere SU cantidad: la comida pasa a Manual (ahí nadie se la toca) y se
    // repone lo que escribió. Las dos cosas del botón, tal cual las pidió Jesús.
    const quererLaPedida = (food) => {
        if (!ajusteAuto) return;
        const idxActual = foods.findIndex(x => idDe(x) === ajusteAuto.foodId);
        setAjusteAuto(null);
        if (idxActual < 0) return;
        setMealMode(mealKey, 'manual');
        const valor = esPorUnidad && esPorUnidad(food)
            ? String(unidadesRedondas(ajusteAuto.pedida, pesoUnidad ? pesoUnidad(food) : 100, food))
            : String(ajusteAuto.pedida);
        updateFoodQuantityDirect(mealKey, idxActual, valor);
    };

    const HeaderInner = (
        <>
            <div className="flex items-center gap-3 min-w-0">
                <div className={`${denso ? 'w-9 h-9 text-sm' : 'w-12 h-12 text-lg'} ${forceExpanded && !denso ? 'hidden lg:flex' : 'flex'} rounded-xl items-center justify-center flex-shrink-0 font-heading font-bold ${isPeri ? 'bg-brand/10 text-brand' : 'bg-muted text-foreground'}`}>
                    {isPeri ? <Zap className={denso ? 'w-4 h-4' : 'w-5 h-5'} /> : info.shortName}
                </div>
                {/* EL TEXTO, MÁS GRANDE. Esta fila es lo que el cliente recorre de arriba
                    abajo para saber qué le queda por hacer, y el objetivo -- los tres
                    números que decide todo lo demás -- iba en 12 px, más pequeño que
                    cualquier otra cosa de la pantalla. El nombre sube a 20 px y el objetivo
                    a 15, que es el tamaño del texto normal. */}
                <div className="min-w-0 flex-1">
                    <div className={`flex ${forceExpanded && !denso ? 'items-start gap-2' : 'items-center gap-2'}`}>
                        {/* TODAS CON SU NOMBRE, SIEMPRE (punto 114). Pasaban dos cosas y las
                            dos las contaba él: el nombre se cortaba en el móvil («COMID…»)
                            porque iba a 24 px en mayúsculas compitiendo en la misma línea con
                            el estado y con Automático/Manual; y cuando la comida se pasaba
                            DESAPARECÍA del móvil, porque el texto largo del exceso le quitaba
                            el sitio a propósito, y quedaba solo el cuadrito «C1». Ahora el
                            nombre tiene su línea entera y el estado se ha bajado a la de los
                            números, así que ya no compiten. */}
                        <h3 className={`font-heading font-bold uppercase tracking-wide text-foreground truncate min-w-0 ${denso ? 'text-base' : 'text-[17px] lg:text-lg'} ${forceExpanded && !denso ? 'max-lg:flex-1 max-lg:whitespace-normal max-lg:normal-case max-lg:tracking-normal max-lg:text-[22px] max-lg:leading-tight max-lg:pt-0.5' : ''}`}>
                            {forceExpanded && !denso && !isPeri && info.name !== 'Comida única' && <span className="lg:hidden">{info.shortName} · </span>}
                            {/* «Intraentreno» y «Postentreno» en el título de la tarjeta (Enrique, 16-10); en la fila
                                de comidas y en la barra sigue siendo «Intra», que es corto. */}
                            {mealKey === 'Intra' ? 'Intraentreno' : mealKey === 'Post' ? 'Postentreno' : info.name}
                        </h3>
                        {/* Bloqueada por el volcado: el candado, que no es un estado sino que
                            esa comida ya no juega (ver estadoDeLaComida). */}
                        {isLocked && <Lock className="w-4 h-4 text-amber-500 flex-shrink-0" />}
                        {/* AUTOMÁTICO / MANUAL EN PEQUEÑO, A LA ALTURA DEL TÍTULO (handoff, 16-10).
                            Va en esta fila, a la derecha, y no en una columna aparte: así el
                            objetivo y la ayuda de debajo tienen el ancho entero. Mismo
                            `setMealMode` y mismos ids que el bloque del ordenador (solo se ve uno). */}
                        {forceExpanded && !denso && !isPeri && !isLocked && setMealMode && (
                            <span className="lg:hidden ml-auto inline-flex gap-0.5 rounded-full bg-muted p-[3px] flex-shrink-0" role="radiogroup" aria-label="Ajuste de cantidades">
                                {[['auto', 'Automático'], ['manual', 'Manual']].map(([modo, texto]) => {
                                    const activo = modo === 'manual' ? mealMode === 'manual' : mealMode !== 'manual';
                                    return (
                                        <button key={modo} type="button" role="radio" aria-checked={activo}
                                            onClick={() => setMealMode(mealKey, modo)} data-testid={`mode-${modo}-${mealKey}`}
                                            className={`min-h-[32px] px-2.5 max-[359px]:px-2 rounded-full text-[13px] max-[359px]:text-xs font-semibold transition-colors ${activo ? 'bg-brand text-[#141414]' : 'text-muted-foreground hover:text-foreground'}`}>
                                            {texto}
                                        </button>
                                    );
                                })}
                            </span>
                        )}
                    </div>
                    {/* EL OBJETIVO Y EL ESTADO, EN LA MISMA LÍNEA (puntos 115 y 116). Los
                        números, sin letras y sin decimales: «53 · 10 · 15», que el orden ya
                        está escrito arriba. Y el estado con su punto y su palabra, en el
                        vocabulario de la parte 2. */}
                    {/* APILADOS EN EL MÓVIL, en la misma línea a partir de `sm`. Un exceso de
                        dos macros («sobran 264 de hidratos y 40 de grasa») no cabe al lado de
                        nada en 390 px: compartiendo línea aplastaba el objetivo hasta dejarlo
                        en «Objetivo…», que es el mismo problema del nombre una fila más
                        arriba. Cada uno en su línea cabe entero y no se corta ninguno. */}
                    <div className="flex flex-col items-start gap-0.5 mt-0.5 sm:flex-row sm:flex-wrap sm:items-baseline sm:justify-between sm:gap-x-3">
                        {/* EL OBJETIVO EN PASTILLAS, EN TODAS LAS COMIDAS (handoff «Crear comida»;
                            Enrique, 16-10: «la a»). Era «Objetivo · 56,2P · 42,5H · 15G» en una
                            línea; ahora cada macro en su pastilla, con la misma cifra y su letra.
                            El texto para lectores de pantalla sigue siendo uno solo. */}
                        {/* Y CON LA COMIDA ABIERTA, FUERA EN EL TELÉFONO (Enrique, 16-10): la barra fija de
                            abajo ya dice cada macro contra su objetivo. En el ordenador se queda. */}
                        <p className={`${forceExpanded && !denso ? 'hidden lg:flex' : 'flex'} flex-wrap items-center gap-1.5 mt-1`} data-testid={`objetivo-${mealKey}`}
                            aria-label={`Objetivo · ${lineaObjetivo}`}>
                            <span className="text-xs text-muted-foreground mr-0.5" aria-hidden="true">Objetivo</span>
                            {(isPeri ? ['P', 'H'] : ['P', 'H', 'G']).map((k) => (
                                <span key={k} aria-hidden="true"
                                    className={`rounded-full bg-muted font-data font-semibold text-foreground ${denso ? 'px-2 py-0.5 text-xs' : 'px-2.5 py-1 text-[13px]'}`}>
                                    {num1(target[k])} {k}
                                </span>
                            ))}
                        </p>
                        {/* AQUÍ IBA LA LÍNEA DE AYUDA DEL MODO EN EL TELÉFONO («Ajusta las cantidades de los
                            alimentos a tus macros» / «Las pones tú...», handoff del 16-10). Se quita
                            (Enrique, 16-10): la pastilla Automático/Manual ya dice el modo. */}
                        {/* CON LA COMIDA ABIERTA, EL ESTADO NO SE REPITE (punto 120). Dentro
                            están los tres macros uno a uno, con su palabra y su barra: decir
                            además «faltan 20 de proteína» aquí arriba es contar lo mismo dos
                            veces, y peor, porque lo resume. Cerrada sí: ahí es lo único que
                            dice en qué punto está esa comida. */}
                        {/* EL INTRA Y EL POST TAMBIÉN LLEVAN SU ESTADO (maqueta de la parte 6:
                            «Intra · Objetivo · 9P · 6H · cuadrada»). Estaban excluidos, así que
                            en una lista de seis tomas dos no decían en qué punto estaban y
                            había que abrirlas para saberlo. Su estado se calcula igual, sólo
                            con P y H, porque en el método la grasa del peri no cuenta
                            (`estadoDeLaComida` ya lo hace con `esPeri`). */}
                        {/* LOS DOS TROZOS, CADA UNO CON SU COLOR (punto 198). El estado puede
                            llevar lo que sobra Y lo que falta, y no son lo mismo: el primero va
                            con su punto y en el naranja del que se pasa, el segundo detrás y en
                            blanco. En el teléfono, si no caben en la línea, el segundo cae solo
                            a la de abajo (`flex-wrap`). */}
                        {!isExpanded && (
                            <span className="text-xs font-bold flex flex-col items-start gap-0.5 sm:items-end sm:text-right"
                                data-testid={`estado-comida-${mealKey}`}>
                                {(estado.partes || [estado]).map((p, i) => (
                                    <span key={i} className={`flex items-start gap-1.5 ${claseDelEstado(p.color)}`}>
                                        {llevaPuntoDeEstado(p.color) && (
                                            <span className="mt-1"><PuntoDeEstado color={p.color} /></span>
                                        )}
                                        {p.texto}
                                    </span>
                                ))}
                            </span>
                        )}
                    </div>
                </div>
            </div>

            {/* El estado ya no vive aquí: se ha bajado a la línea del objetivo, junto al
                nombre, y es el mismo en móvil y en escritorio (punto 116). Estaba suelto en
                esta fila y por eso el nombre se quedaba sin sitio. */}
            {/* Con el día entero desplegado el modo va aquí, en pequeño: la banda de
                "Modo de cálculo" repetida seis veces no cabía, pero esconderla dejaba
                sin Automático/Manual a las comidas que aún no tienen alimentos. */}
            {/* Y AQUÍ SE HABÍA QUEDADO LA FRASE VIEJA (30-08). El punto 191 sacó «Yo te
                ajusto las cantidades» del singular y le puso a qué ajusta, pero solo en la
                tarjeta normal: en la vista «Todo seguido» el modo se pinta aquí, con la
                explicación metida en un `title`, y ahí seguía la de antes. Mismas palabras
                que abajo, para que la app no diga dos cosas. */}
            {denso && !isPeri && !isLocked && setMealMode && (
                <div className="inline-flex rounded-lg bg-muted p-0.5 flex-shrink-0" title="Automático: ajusta las cantidades de los alimentos a tus macros. Manual: las pones tú y lo compensas en el día">
                    <button className={`px-2.5 py-1 text-[11px] font-bold rounded-md transition-colors ${mealMode !== 'manual' ? 'bg-brand text-white' : 'text-muted-foreground hover:text-foreground'}`}
                        onClick={() => setMealMode(mealKey, 'auto')} data-testid={`mode-auto-${mealKey}`}>Automático</button>
                    <button className={`px-2.5 py-1 text-[11px] font-bold rounded-md transition-colors ${mealMode === 'manual' ? 'bg-brand text-white' : 'text-muted-foreground hover:text-foreground'}`}
                        onClick={() => setMealMode(mealKey, 'manual')} data-testid={`mode-manual-${mealKey}`}>Manual</button>
                </div>
            )}
            {!forceExpanded && (isExpanded ? <ChevronUp className="w-5 h-5 text-muted-foreground flex-shrink-0" /> : <ChevronDown className="w-5 h-5 text-muted-foreground flex-shrink-0" />)}
        </>
    );

    return (
        <div className={`surface overflow-hidden ${isPeri ? 'border-l-4 border-l-brand' : ''} ${isLocked ? 'opacity-70' : ''} ${!forceExpanded && !isExpanded ? 'surface-hover' : ''} ${yaEsta && !isExpanded ? 'opacity-70' : ''}`} data-testid={`meal-card-${mealKey}`}>
            {/* Header */}
            {forceExpanded ? (
                <div className={`${denso ? 'p-3 sm:p-3.5' : 'p-4 sm:p-5 max-lg:pb-3'} flex items-center justify-between gap-3 border-b border-border`}>{HeaderInner}</div>
            ) : (
                <button className="w-full text-left p-3.5 sm:p-4 pb-1.5 flex items-center justify-between gap-3"
                    onClick={() => setExpandedMeals(prev => ({ ...prev, [mealKey]: !isExpanded }))}>
                    {HeaderInner}
                </button>
            )}

            {/* «+ CREATINA», DEBAJO DE LOS MACROS DE SU COMIDA (maqueta de la parte 6, y el
                punto 190: desde aquí se llega a Suplementos, que es lo que faltaba).
                El mismo dato que en Inicio y con la misma regla (lib/suplementosDelDia):
                sólo el nombre, y el intra y el post no llevan ninguno.

                EN GRIS Y CON SU FLECHA, NO EN EL NARANJA DE LA MARCA (punto 202 del 28-08).
                El naranja era correcto por la regla -- es el color de lo que se toca -- pero
                lo gastaba en lo que menos corre: «lo primero que ves es la creatina, que no
                tiene ninguna prisa; sobran 6,5 de hidratos sí». La tarjeta entera ya se
                pulsa sin ser naranja, así que la › basta para decir que lleva a otro sitio,
                que es el riesgo que el punto 203 dejaba escrito.

                Y VA FUERA DEL BOTÓN DE LA COMIDA, igual que en Inicio: la tarjeta abre la
                comida y esto lleva a Suplementos. Un botón dentro de otro ni es HTML válido
                ni deja elegir destino. El margen de la izquierda lo alinea con el nombre,
                por debajo del cuadrito. */}
            {!isPeri && (suplementos || []).length > 0 && (
                <button type="button" onClick={() => irASuplementos && irASuplementos()}
                    data-testid={`suplementos-comida-${mealKey}`}
                    // SIN SUBIRSE A LA RAYA (Jesús, reunión del 25-09: «la comida uno sale cortada,
                    // los suplementos»). Con la tarjeta desplegada la cabecera lleva una raya
                    // abajo, y el `-mt-1` subía esta línea encima: se leía partida. Ahora va
                    // debajo, con su aire. Y MÁS CLARA («con otro tono, más blanco, que
                    // contraste un poco más»).
                    className={`${denso ? 'px-3 sm:px-3.5' : 'px-3.5 sm:px-4'} ${forceExpanded ? 'pt-2 pb-2' : 'pb-1'} flex items-center gap-1 text-xs text-foreground/80 text-left hover:text-foreground transition-colors`}>
                    <span className={denso ? 'ml-12' : 'ml-[3.75rem]'}>+ {suplementos.join(' · ')}</span>
                    <ChevronRight className="w-3.5 h-3.5 flex-shrink-0" />
                </button>
            )}
            {/* Aquí iba OTRA VEZ el objetivo, solo para el teléfono y a 17 px. Ahora está en
                la línea de debajo del nombre, con el estado al lado y en los dos tamaños, así
                que esto era el mismo dato dos veces en la misma tarjeta.
                La palabra «Objetivo» delante se queda (Francisco, 17-08): son el objetivo y
                no lo que lleva puesto, y sin decirlo dos comidas con los mismos números se
                veían idénticas y una decía «te pasas» y la otra «cuadrada». */}

            {/* «DESHACER», EN LA TARJETA Y SIN CUENTA ATRAS (Jesus, 6-09-2026).
                VA FUERA DE `isExpanded` a proposito: vaciar la comida la deja sin nada
                dentro y con la tarjeta cerrada, que es justo cuando hace falta poder volver.
                Y fuera del boton de la cabecera, que es lo que abre y cierra: un boton
                dentro de otro ni es HTML valido ni deja elegir cual se pulsa. */}
            {deshacer && (
                <div className={`${denso ? 'px-3 sm:px-3.5' : 'px-3.5 sm:px-4'} pb-2.5 pt-1`}>
                    <div className="flex items-center justify-between gap-3 rounded-xl border border-border bg-muted/40 px-3 py-2">
                        <span className="text-xs text-muted-foreground min-w-0">{deshacer.texto}</span>
                        <button type="button" onClick={deshacer.onDeshacer}
                            data-testid={`deshacer-${mealKey}`}
                            className="flex-shrink-0 text-xs font-bold text-brand hover:underline underline-offset-4">
                            Deshacer
                        </button>
                    </div>
                </div>
            )}

            {isExpanded && (
                <div className={forceExpanded ? (denso ? 'p-3 sm:p-3.5 space-y-3' : 'p-4 sm:p-5 max-lg:pt-3 space-y-4') : 'px-3.5 sm:px-4 pb-4 pt-1 space-y-3'}>
                    {/* Modo de cálculo. En denso no va aquí: está en la cabecera de la comida. */}
                    {/* Los números primero, que es a lo que se entra: a mover gramos hasta que
                        cuadre. Los controles van debajo. */}
                    <div className={forceExpanded && !denso ? 'hidden lg:block' : undefined}>
                        <MealProgressBars mealKey={mealKey} getMealTarget={getMealTarget}
                            calculateMealMacros={calculateMealMacros} hasFoods={foods.length > 0}
                            neutra={status === 'neutra'} />
                    </div>

                    {/* «AJUSTE DE CANTIDADES», NO «MODO DE CÁLCULO» (punto 124 del 26-08).
                        Y la etiqueta encima con los dos botones debajo, como Entreno/Descanso.

                        Debajo iba una frase que decía lo único que hay que entender -- «Yo te
                        ajusto las cantidades» -- y estaba escondida en `lg`: en el móvil no se
                        veía nunca. La tapaban dos palabras de jerga. Ahora la jerga se va y la
                        frase se queda, en los dos tamaños. */}
                    {!isPeri && !isLocked && setMealMode && !denso && (
                        <div className={`rounded-xl bg-muted/50 px-3.5 py-3 ${forceExpanded ? 'hidden lg:block' : ''}`}>
                            <p className="text-[11px] font-bold text-muted-foreground uppercase tracking-wider">Ajuste de cantidades</p>
                            {/* A TODO EL ANCHO EN EL TELÉFONO (15-10): dos mitades de 44 px de alto,
                                que se aciertan con el pulgar; desde sm vuelve a ser compacto. */}
                            <div className="grid grid-cols-2 sm:inline-grid rounded-lg bg-card p-0.5 border border-border mt-2" role="radiogroup" aria-label="Ajuste de cantidades">
                                <button role="radio" aria-checked={mealMode !== 'manual'}
                                    className={`min-h-[44px] sm:min-h-0 px-3 py-1.5 text-sm sm:text-xs font-bold rounded-md transition-colors ${mealMode !== 'manual' ? 'bg-brand text-white' : 'text-muted-foreground hover:text-foreground'}`}
                                    onClick={() => setMealMode(mealKey, 'auto')} data-testid={`mode-auto-${mealKey}`}>Automático</button>
                                <button role="radio" aria-checked={mealMode === 'manual'}
                                    className={`min-h-[44px] sm:min-h-0 px-3 py-1.5 text-sm sm:text-xs font-bold rounded-md transition-colors ${mealMode === 'manual' ? 'bg-brand text-white' : 'text-muted-foreground hover:text-foreground'}`}
                                    onClick={() => setMealMode(mealKey, 'manual')} data-testid={`mode-manual-${mealKey}`}>Manual</button>
                            </div>
                        </div>
                    )}

                    {isLocked && (
                        <div className="flex items-center gap-2 text-xs text-amber-700 dark:text-amber-400 bg-amber-50 dark:bg-amber-500/10 border border-amber-200 dark:border-amber-500/25 rounded-xl px-3 py-2">
                            <Lock className="w-3.5 h-3.5 shrink-0" />
                            <span>Bloqueada: los macros del día están volcados en otra comida. Deshaz el volcado para editarla.</span>
                        </div>
                    )}

                    {/* Empty states. En denso (día entero desplegado) las tres maneras de
                        empezar caben en una sola fila: el bloque alto repetido seis veces
                        convertía la pantalla en un pasillo de botones naranjas.

                        POR QUÉ ESTOS BOTONES LLEVAN CUATRO COSAS PEGADAS A LA CLASE.
                        `btn-outline-brand` está pensado para un botón ancho y trae `px-5`:
                        45 px de padding. En una rejilla de tres columnas a 390 px el botón
                        mide 101 px, así que al texto le quedaban 34 px y «Lo hago yo» salía
                        partido EN TRES LÍNEAS, una por palabra, con el botón estirado a 50 px
                        de alto (Francisco, 26-08).

                        Y no basta con `px-2 whitespace-nowrap`: al dejar de partirse, el
                        texto empujaba y el que se encogía era el ICONO, que en un flex sin
                        `flex-shrink-0` se aplasta a cero. La llave de «Lo hago yo» y la
                        estrella de «Favoritas» desaparecían y sólo sobrevivía la de
                        «Repetir», que es el rótulo más corto. Cambiado un fallo por otro.
                        Así que el texto baja a `text-xs` en el móvil (vuelve a `sm` en
                        cuanto hay ancho) y el icono se declara irreductible. */}
                    {foods.length === 0 && !isPeri && !isLocked && (
                        denso ? (
                            // Mismas tres opciones y con su nombre entero; solo cambia que caben
                            // en una fila, con "Sugiéreme un menú" del doble de ancho porque es
                            // la principal. En móvil pasan a dos filas para no recortar el texto.
                            <div className="grid grid-cols-2 sm:grid-cols-[2fr_1fr_1fr] gap-2">
                                <button className="btn-brand h-11 flex items-center justify-center gap-1.5 px-2 text-xs sm:text-sm leading-tight uppercase tracking-wide [&>svg]:flex-shrink-0"
                                    onClick={() => loadMenuOptions(mealKey)} data-testid={`menu-options-${mealKey}`}>
                                    <Zap className="w-4 h-4" /> Sugiéreme un menú
                                </button>
                                <button className="btn-outline-brand h-11 flex items-center justify-center gap-1.5 px-2 whitespace-nowrap text-xs sm:text-sm [&>svg]:flex-shrink-0"
                                    onClick={() => setBuildMealModal({ open: true, mealKey, mode: 'normal' })} data-testid={`build-meal-${mealKey}`}>
                                    <Pointer className="w-4 h-4" /> Elijo yo
                                </button>
                                <button className="btn-outline-brand h-11 col-span-2 sm:col-span-1 flex items-center justify-center gap-1.5 px-2 whitespace-nowrap text-xs sm:text-sm [&>svg]:flex-shrink-0"
                                    onClick={() => openRepeatModal(mealKey)} data-testid={`repeat-meal-${mealKey}`}>
                                    <RefreshCw className="w-4 h-4" /> Repetir
                                </button>
                            </div>
                        ) : (
                        <ComidaVacia mealKey={mealKey} nombre={info.name} objetivo={target}
                            sangria={forceExpanded ? '-mx-4 sm:-mx-5' : '-mx-3.5 sm:-mx-4'} conRaya={!forceExpanded}
                            loadMenuOptions={loadMenuOptions} setBuildMealModal={setBuildMealModal}
                            openRepeatModal={openRepeatModal}
                            recientes={recientesDeLaComida ? recientesDeLaComida(mealKey) : []}
                            onRepetir={repetirDeOtroDia}
                            favoritas={favoritasDeLaComida(favoritasDeComida, mealKey, periFavorito)} onAplicarFavorita={aplicarFavoritaEn}
                            abrirFavoritas={abrirFavoritasDeComida} />
                        )
                    )}
                    {/* EL INTRA Y EL POST NO SE COMPORTAN IGUAL (Francisco, 25-08).
                        En los dos se ha quitado el botón de menús: la biblioteca son comidas
                        de clientes y en el peri no pinta nada.
                        - INTRA: «Prepárame el intra» abre el constructor con sus categorías
                          ya puestas, así que la lista sale sola y en el orden del método
                          (MAP primero, luego la ciclodextrina). Ahí sí prepara.
                        - POST: se monta como una comida normal. Tenía el mismo botón y era
                          mentira: el post abre sin categorías y lo primero que salía era
                          «Selecciona una categoría arriba», o sea el buscador de siempre.
                          Antes que prometer lo que no hace, se llama por su nombre.
                        «Lo hago yo» abre igualmente en modo post, que es lo que hace valer
                        sus reglas (universo de la categoría 25 y sin objetivo de grasa). */}
                    {/* EL PERI VACÍO: CREAR, Y DEBAJO LO DE SIEMPRE (Jesús, artefacto «El sugeridor
                        del perientreno», 25-09). «El intra y el post son las dos únicas comidas de
                        la app que no se pueden sugerir.» El botón nuevo va el primero y en naranja,
                        como «Sugiéreme un menú», y MONTA LA COMIDA DE UN TOQUE (Francisco, 25-09):
                        el primero de Proteína y el primero de Hidratos del orden del método, cada
                        uno con la cantidad que cubre el 100 % (core/peri_sugerencias).
                        ESTO REVIERTE LO DE CÉSAR DEL 21-09 («el intra se hace manual»): manda lo
                        más reciente. Con el objetivo del peri a cero no se enseña: no hay nada
                        que crear. «Lo hago yo» abre la ventana en su modo, con sus reglas. */}
                    {/* «NO QUIERO INTRA», FUERA DE AQUÍ (Jesús, 27-09 22:43: «si te equivocas tocando,
                        cambia toda la configuración del día; eso no se puede quitar desde la
                        pantalla principal»). Queda «Crear intra»; con el intra hecho, «Cambiar
                        intra» debajo. Lo de antes: EL INTRA, DOS SALIDAS (Jesús, «El intra, paso
                        a paso», 27-09): «Crear intra», que abre la hoja de los tres pasos, y «No
                        quiero intra». «Fuera los tres
                        botones: Lo hago yo ya es el buscador de dentro, y Repetir y Favoritas
                        sobran si el intra se queda guardado solo». REVIERTE el «Crear de un
                        toque» del 25-09: manda lo más reciente. */}
                    {foods.length === 0 && mealKey === 'Intra' && !isLocked && (
                        // COMO LA TARJETA DE «SUGIÉREME UN MENÚ» (Enrique, 16-10): el título grande,
                        // la frase de los tres pasos y la flecha abajo a la derecha.
                        <button type="button" onClick={() => setBuildMealModal({ open: true, mealKey, mode: 'intra' })}
                            data-testid="crear-peri-Intra"
                            className="w-full min-h-[132px] rounded-2xl bg-brand text-[#141414] p-4 flex flex-col gap-2 text-left hover:brightness-105 transition">
                            <span className="font-heading text-[25px] font-extrabold leading-none">Crear intra</span>
                            <span className="text-[13px] font-medium leading-snug flex-1">Tu bebida intraentreno, en 3 pasos.</span>
                            <span className="w-8 h-8 rounded-full bg-[#141414] text-brand flex items-center justify-center self-end">
                                <ArrowRight className="w-4 h-4" strokeWidth={2.75} />
                            </span>
                        </button>
                    )}
                    {/* EL POSTENTRENO VACÍO, COMO UNA COMIDA NORMAL (Enrique, 16-10): las mismas dos
                        tarjetas y debajo «Repetir de otro día» y los favoritos, con sus textos:
                        «Sugiéreme un postentreno» y sus postentrenos favoritos (Jesús, 27-09: «te
                        tiene que meter en sus postentrenos favoritos, no en las comidas
                        favoritas»). Sustituye a la fila de «Elijo yo · Repetir · Favoritos». */}
                    {foods.length === 0 && isPeri && mealKey !== 'Intra' && !isLocked && (() => {
                        const obj = getMealTarget ? getMealTarget(mealKey) : null;
                        const hayObjetivo = Boolean(obj && ((obj.P || 0) > 0 || (obj.H || 0) > 0));
                        const sugerir = !hayObjetivo ? null
                            : sugerirPost ? { titulo: 'Sugiéreme un postentreno', texto: 'Te proponemos un postentreno que cuadra tus macros.',
                                onClick: () => sugerirPost(mealKey), testId: `sugerir-post-${mealKey}` }
                            : crearPeri ? { titulo: creandoPeri === mealKey ? 'Creando...' : 'Crear post', texto: 'Te preparamos el postentreno de un toque.',
                                onClick: () => crearPeri(mealKey), testId: `crear-peri-${mealKey}`, ocupado: creandoPeri === mealKey }
                            : null;
                        return (
                            <ComidaVacia mealKey={mealKey} nombre="postentreno" objetivo={target}
                                sangria={forceExpanded ? '-mx-4 sm:-mx-5' : '-mx-3.5 sm:-mx-4'} conRaya={!forceExpanded}
                                sugerir={sugerir} modo="post" tituloFavoritas="Mis postentrenos favoritos"
                                setBuildMealModal={setBuildMealModal} openRepeatModal={openRepeatModal}
                                recientes={recientesDeLaComida ? recientesDeLaComida(mealKey) : []}
                                onRepetir={repetirDeOtroDia}
                                favoritas={favoritasDeLaComida(favoritasDeComida, 'Post', periFavorito)}
                                onAplicarFavorita={aplicarFavoritaEn} abrirFavoritas={abrirFavoritasDeComida} />
                        );
                    })()}

                    {/* «PUESTO SOLO · CAMBIARLO · HOY NO», FUERA (Jesús, 27-09 22:40: «no hay que
                        poner eso de puesto solo, cambiarlo y quitarlo; además sale mal: he hecho el
                        intra, he puesto hoy no y me ha salido esto»). El intra de siempre se sigue
                        poniendo solo; para cambiarlo, «Cambiar intra» (22:43). */}
                    {/* Y EL POST IGUAL (Jesús, 27-09 22:52: «sugiéreme un postentreno y debajo cambiar
                        postentreno; tiene que ser como el intra»). */}
                    {foods.length > 0 && mealKey === 'Intra' && !isLocked && (
                        <button type="button"
                            onClick={() => setBuildMealModal({ open: true, mealKey, mode: 'intra' })}
                            className="w-full py-2.5 rounded-xl border border-border text-sm font-semibold text-foreground hover:border-brand/50 flex items-center justify-center gap-1.5"
                            data-testid="cambiar-Intra">
                            <Zap className="w-4 h-4 text-brand" /> Cambiar intra
                        </button>
                    )}
                    {/* EL POST YA GUARDADO (Jesús, 27-09 22:57: «si el postentreno ya está guardado:
                        sugiéreme un postentreno y debajo cambiar postentreno o favoritos; repetir no,
                        porque ya estaría repetido»). Sus favoritos son los suyos; los de otros
                        usuarios, en «Sugiéreme». */}
                    {/* CON EL POST YA HECHO, SOLO «CAMBIAR POSTENTRENO», COMO «CAMBIAR INTRA» (Enrique,
                        16-10). Fuera «Sugiéreme un postentreno» y «Favoritos» de aquí: revierte la
                        nota de Jesús del 27-09 22:57, manda lo más reciente. Guardarlo como favorito
                        sigue en el «···» de abajo. */}
                    {foods.length > 0 && isPeri && mealKey !== 'Intra' && !isLocked && (
                        <button type="button"
                            onClick={() => setBuildMealModal({ open: true, mealKey, mode: 'post' })}
                            className="w-full py-2.5 rounded-xl border border-border text-sm font-semibold text-foreground hover:border-brand/50 flex items-center justify-center gap-1.5"
                            data-testid={`cambiar-${mealKey}`}>
                            <Zap className="w-4 h-4 text-brand" /> Cambiar postentreno
                        </button>
                    )}

                    {/* Ingredients */}
                    {/* Sin el margen que le dejaban los bloques ocultos de encima en el teléfono. */}
                    {foods.length > 0 && (
                        <div className={`space-y-3 ${forceExpanded && !denso && !isLocked && !isPeri ? 'max-lg:!mt-0' : ''}`}>
                            <div>
                                {/* «−/+ = gramos» Y YA (punto 127). Ponía «↑ = prioridad», y
                                    prioridad hace pensar que Cuadrar reparte por ese orden. No
                                    es verdad: la flecha solo coloca el alimento más arriba. */}
                                <div className="flex items-center justify-between mb-2">
                                    <p className="caption">Ingredientes</p>
                                </div>
                                {/* ORDENANDO (punto 126): las flechas solo aparecen cuando se
                                    pide, y mientras tanto hay una barra que dice qué está
                                    pasando y cómo salir. Así cada alimento recupera el ancho
                                    que ocupaba la flecha, que es lo que hacía que «Fiambre de
                                    pechuga de pavo de buena calidad (más del 85 %)» cayera a
                                    dos líneas. */}
                                {ordenando && (
                                    <div className="flex items-center justify-between gap-3 rounded-xl bg-brand/10 border border-brand/40 px-3 py-2 mb-2"
                                        data-testid={`ordenando-${mealKey}`}>
                                        <span className="text-xs font-bold text-brand">Ordena los alimentos</span>
                                        <button onClick={() => setOrdenando(false)} data-testid={`ordenar-listo-${mealKey}`}
                                            className="text-xs font-bold text-brand hover:underline">Listo</button>
                                    </div>
                                )}
                                <div className="space-y-1.5">
                                    {foods.map((food, idx) => (
                                        <React.Fragment key={idx}>
                                            <IngredientRow food={food} idx={idx} mealKey={mealKey} isLocked={isLocked}
                                                isEditing={editingQuantity.mealKey === mealKey && editingQuantity.foodIndex === idx}
                                                // En el intra y el post, de uno en uno (Jesús, «El sugeridor del perientreno»,
                                                // 25-09): revierte el «de a 5» del 27-08, manda lo más reciente.
                                                // Lo que va por unidades sigue moviendo unidades.
                                                increment={isPeri && !esPorUnidad(food) ? 1 : getQuantityIncrement(food)}
                                                moveFoodUp={moveFoodUp} removeFood={removeFood}
                                                updateFoodQuantity={updateFoodQuantityVigilada} updateFoodQuantityDirect={updateFoodQuantityDirectVigilada}
                                                setEditingQuantity={setEditingQuantity} formatFoodQuantity={formatFoodQuantity}
                                                esPorUnidad={esPorUnidad} pesoUnidad={pesoUnidad}
                                                acumFamilias={acumFamilias} ordenando={ordenando} />
                                            {/* LA PUERTA: el recuadro del autoajuste, pegado al ingrediente tocado.
                                                No es un toast: se queda hasta que el cliente decida, con los números
                                                reales de lo que pidió y lo que la app dejó (doc 21-08, apartado 14). */}
                                            {ajusteAuto && idDe(food) === ajusteAuto.foodId && (
                                                <div className="rounded-xl border border-border bg-muted/50 px-3 py-2.5 space-y-2"
                                                    data-testid={`ajuste-auto-${mealKey}-${idx}`}>
                                                    {/* EN NUESTRA VOZ Y SIN GÉNERO (fichas 47, 49 y 50 del parte de
                                                        Gonzalo, 5-09). Decía «Te he ajustado ... Si quieres los 30 g» y
                                                        «Los quiero en 30 g»: la primera persona no es la de la app
                                                        (la regla de las voces: nosotros), y el «los» no concordaba
                                                        con las claras ni con las tortitas. Ahora la frase no lleva
                                                        artículo que concordar. */}
                                                    <p className="text-[13px] leading-snug text-foreground">
                                                        Hemos dejado {food.nombre} en <span className="font-bold">{fmtCant(food, ajusteAuto.ajustada)}</span> para
                                                        que la comida cuadre. Si prefieres {fmtCant(food, ajusteAuto.pedida)}, lo compensas en el resto del día.
                                                    </p>
                                                    <div className="flex flex-wrap items-center gap-2">
                                                        <button className="btn-brand px-3 py-1.5 text-xs rounded-lg"
                                                            onClick={() => quererLaPedida(food)}
                                                            data-testid={`ajuste-auto-quiero-${mealKey}`}>
                                                            Quiero {fmtCant(food, ajusteAuto.pedida)}
                                                        </button>
                                                        <button className="px-2 py-1.5 text-xs text-muted-foreground hover:text-foreground transition-colors"
                                                            onClick={() => setAjusteAuto(null)}
                                                            data-testid={`ajuste-auto-dejar-${mealKey}`}>
                                                            Dejarlo en {fmtCant(food, ajusteAuto.ajustada)}
                                                        </button>
                                                    </div>
                                                </div>
                                            )}
                                        </React.Fragment>
                                    ))}
                                </div>
                            </div>
                            {/* CUADRAR, ANCHO Y EXPLICADO (punto 125). Era un botón pequeño
                                perdido entre otros cuatro, y lo que hace lo contaba un
                                `title`: en el móvil no hay ratón, así que ningún cliente lo
                                había leído nunca. La frase baja debajo del botón, visible.
                                (La que él escribe no existía: la de antes hablaba de mínimos.) */}
                            {/* «LO BAJÉ DE TODO A LA VEZ» (31-08-2026, la nota de voz de Jesús).
                                Aplicar una favorita o repetir un día recuadra varias comidas de
                                golpe, y ahí no se puede parar a preguntar de dónde bajar cada
                                una. Se baja en proporción y la comida queda marcada aquí, para
                                que quien quiera elegir sepa que hay algo que elegir: sin esta
                                línea, la decisión seguiría tomándola la app en silencio. */}
                            {/* Y NUNCA ENCIMA DE «YA ESTÁ CUADRADA» (Francisco, 22-09): tras
                                cuadrar el día entero la comida quedaba dentro y el cartel seguía
                                diciendo «sobran 8,2 aunque esté todo al mínimo»; era la marca de
                                antes, que nadie había borrado. Lo que se ve manda: dentro de
                                margen no hay nada que elegir. */}
                            {!isLocked && onCuadrar && eleccionPendiente && status !== 'cuadrada' && status !== 'neutra' && (
                                <button onClick={() => onCuadrar(mealKey)}
                                    data-testid={`eleccion-pendiente-${mealKey}`}
                                    className="w-full text-left rounded-xl border border-brand/40 bg-brand/5 px-3 py-2.5 mb-2">
                                    <span className="block text-xs font-bold text-brand">
                                        {/* «NO SÉ QUÉ QUIERE DECIR ESO» (Jesús, vídeo del
                                            20-09, sobre esta misma línea: «hay un aviso que
                                            dice aquí sobraba y lo bajé todo de una vez... esto
                                            hay que revisarlo mucho»).

                                            La frase venía de su nota de voz del 31-08 y la
                                            copié literal, pero fuera de aquella conversación no
                                            se entiende: no dice QUÉ se bajó ni DE DÓNDE. Ahora
                                            lo dice: se bajó un poco de cada alimento. Manda lo
                                            más reciente, que es él sin contexto delante. */}
                                        {pendienteEsQuitar
                                            ? (loQueSobraAqui
                                                ? `Aquí sobran ${loQueSobraAqui} aunque esté todo al mínimo`
                                                : 'Aquí sobra aunque esté todo al mínimo')
                                            : (loQueSobraAqui
                                                ? `Sobraban ${loQueSobraAqui}, y bajé un poco de cada alimento`
                                                : 'Sobraba, y bajé un poco de cada alimento')}
                                    </span>
                                    <span className="block text-xs text-muted-foreground mt-0.5">
                                        {pendienteEsQuitar
                                            /* Y SE DICE POR QUÉ HAY QUE QUITAR ALGO: bajando
                                               gramos ya no se puede, que es lo que significa
                                               «todo al mínimo» y nadie tiene por qué deducir. */
                                            ? 'Bajando gramos ya no cuadra. Toca para elegir qué alimento quitar'
                                            : 'Toca si prefieres bajarlo de uno solo'}
                                    </span>
                                </button>
                            )}
                            {/* Y SI YA CUADRA, EL BOTÓN SE VA (JDG, Trello 21-09): «si ya la he
                                cuadrado el botón de cuadrar debería desaparecer». Con la comida
                                dentro de margen no hay nada que cuadrar, y pulsarlo la recalculaba
                                desde cero y la dejaba distinta (47,5 → 47,2), que él leía como que
                                la descuadraba. En su lugar, una línea que dice que ya está. */}
                            {!isLocked && onCuadrar && status === 'cuadrada' && foods.length > 0 && (
                                <p className="text-xs text-center text-emerald-400 font-semibold py-1"
                                   data-testid={`ya-cuadrada-${mealKey}`}>
                                    ✓ Esta comida ya está cuadrada
                                </p>
                            )}
                            {!isLocked && onCuadrar && status === 'neutra' && foods.length > 0 && (
                                <div className="text-center py-1" data-testid={`dia-ya-cuadra-${mealKey}`}>
                                    <p className="text-xs text-muted-foreground font-semibold">
                                        El total del día ya cuadra
                                    </p>
                                    <button type="button" onClick={() => onCuadrar(mealKey)}
                                        data-testid={`cuadrar-solo-${mealKey}`}
                                        className="text-xs text-muted-foreground underline underline-offset-2 mt-1">
                                        Cuadrar solo esta comida
                                    </button>
                                </div>
                            )}
                            {!isLocked && onCuadrar && status !== 'cuadrada' && status !== 'neutra' && (
                                <div>
                                    <button onClick={() => onCuadrar(mealKey)} data-testid={`cuadrar-${mealKey}`}
                                        className="btn-brand w-full py-3 rounded-xl text-sm font-bold">
                                        Cuadrar
                                    </button>
                                </div>
                            )}
                            {/* Y LO DEMÁS, DENTRO DEL «···» (punto 126). Estaban los cinco a la
                                vista -- Automático/Manual, ver detalles, Cuadrar, la estrella y
                                Vaciar -- compitiendo con lo único que se viene a hacer aquí. */}
                            <div className="flex items-center gap-2">
                                <button className="flex-1 min-h-[48px] py-2.5 rounded-xl border-[1.5px] border-dashed border-white/20 text-brand font-semibold text-[15px] hover:bg-brand/5 hover:border-brand/50 disabled:opacity-40 disabled:cursor-not-allowed flex items-center justify-center gap-1.5 transition-colors" disabled={isLocked} onClick={() => setBuildMealModal(isPeri
                                    // En el intra y el post, su hoja de pasos, con lo que ya lleva marcado (27-09).
                                    ? { open: true, mealKey, mode: mealKey === 'Intra' ? 'intra' : 'post' }
                                    : { open: true, mealKey, startStep: 2 })} data-testid={`anadir-ingrediente-${mealKey}`}>
                                    <Plus className="w-4 h-4" /> Añadir ingrediente
                                </button>
                                {/* «COMPARTIR» BAJO CADA COMIDA, FUERA (Jesús, 27-09): las comidas se
                                    comparten solas cuando el día está cuadrado (NutritionPage). */}
                                {!isLocked && (
                                    <MenuDeLaPantalla etiqueta="Más opciones de esta comida" opciones={[
                                        foods.length > 1 && { id: `ordenar-${mealKey}`, texto: 'Ordenar los alimentos',
                                            icono: ArrowUpDown, al: () => setOrdenando(true) },
                                        // Guardar SOLO esta comida (25-08). Antes había que
                                        // guardar el día entero y borrar lo que sobraba.
                                        abrirFavoritasDeComida && { id: `fav-${mealKey}`, texto: 'Guardar como favorita',
                                            icono: Star, al: () => abrirFavoritasDeComida(mealKey) },
                                        { id: `vaciar-${mealKey}`, texto: 'Vaciar la comida', icono: Trash2,
                                            peligro: true, al: () => clearMeal(mealKey) },
                                    ]} />
                                )}
                            </div>
                        </div>
                    )}

                    {forceExpanded && !denso && (
                        <>
                            <BarraMacrosFija mealKey={mealKey} getMealTarget={getMealTarget}
                                calculateMealMacros={calculateMealMacros} hasFoods={foods.length > 0}
                                neutra={status === 'neutra'} status={status}
                                nombre={isPeri ? info.name : `${info.shortName} · ${info.name}`} />
                            {/* El hueco que ocupa la barra, para poder llegar al último alimento. Sin
                                alimentos no hay último al que llegar: la tarjeta se ajusta a lo que
                                tiene (Enrique, 16-10, con «Crear intra»), y el margen de abajo de la
                                página ya deja sitio a la barra. */}
                            {foods.length > 0 && <div className="lg:hidden h-24" aria-hidden="true" />}
                        </>
                    )}

                    {/* «VOLCAR LOS MACROS AQUÍ», FUERA (Enrique, 16-10: «no sé cómo funciona y es
                        confuso»). Metía en esta comida todo lo que le quedaba al día y bloqueaba
                        las demás. La lógica sigue en NutritionPage (`handleVolcarToMeal`) y el aviso
                        de «Deshacer volcado» también, para los días que ya lo tengan puesto. */}
                </div>
            )}
        </div>
    );
};

export default MealCard;
